import { useState, useEffect, useRef } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { userLsKey } from "@/lib/userScopedStorage";
import { useLocation } from "wouter";
import { useAudioPlayer } from "@/hooks/useAudioPlayer";
import { useAudioMixer } from "@/hooks/useAudioMixer";
import { useTextToSpeech } from "@/hooks/useTextToSpeech";
import { base64ToBlob } from "@/lib/utils";
import { apiRequest, queryClient } from "@/lib/queryClient";
import type { MeditationPlaylist, MeditationSong, CustomMeditation, MeditationSession, UserPreferences, MnmCategory, PlaylistShare, Friendship } from "@shared/schema";
import { useToast } from "@/hooks/use-toast";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Play, Pause, SkipForward, SkipBack, Square, Volume2, Settings, Youtube, Clock, Calendar as CalendarIcon, Star, Search, Filter, Music, Mic, X, Tag, Plus, ListMusic, Trash2, GripVertical, Edit, Share2, Users, ExternalLink, AlertCircle, ChevronDown } from "lucide-react";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent } from "@dnd-kit/core";
import { SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useIsMobile } from "@/hooks/use-mobile";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { NumberField } from "@/components/ui/number-field";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { startOfDay, startOfWeek, startOfMonth, endOfDay, format } from "date-fns";

// Helper function to extract YouTube video ID from various URL formats
function extractYouTubeVideoId(url: string): string | null {
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\s?]+)/,
    /youtube\.com\/v\/([^&\s?]+)/,
    /youtube\.com\/shorts\/([^&\s?]+)/,
  ];
  
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && match[1]) {
      return match[1];
    }
  }
  return null;
}

// Check if URL is a YouTube URL
function isYouTubeUrl(url: string): boolean {
  return url.includes('youtube.com') || url.includes('youtu.be');
}

// Library item type for combined view
interface MeditationLibraryItem {
  id: string;
  type: 'song' | 'custom';
  title: string;
  artist?: string | null;
  subject?: string | null;
  isStarred: boolean;
  tags?: string[] | null;
  url?: string;
  duration?: number | null;
  playlistId?: string;
  playlistName?: string | null;
  librarySortOrder: number;
  mnmCategoryId?: string | null;
  contentType?: string | null;
}

// Sortable Playlist Row Component
function SortablePlaylistRow({ 
  playlist, 
  onPlay, 
  onEdit,
  onDelete,
  onShare,
  isMobile
}: { 
  playlist: MeditationPlaylist; 
  onPlay: (playlist: MeditationPlaylist, songs: MeditationSong[]) => void;
  onEdit: () => void;
  onDelete: () => void;
  onShare: () => void;
  isMobile: boolean;
}) {
  const { data: playlistSongs = [] } = useQuery<MeditationSong[]>({
    queryKey: ["/api/meditation/playlists", playlist.id, "songs"],
  });

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: playlist.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div 
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-3 p-3 border rounded-lg hover:bg-accent/50 transition-colors group"
      data-testid={`playlist-row-${playlist.id}`}
    >
      {/* Drag Handle */}
      {!isMobile && (
        <div
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing touch-none"
          data-testid={`drag-handle-${playlist.id}`}
        >
          <GripVertical className="w-4 h-4 text-muted-foreground" />
        </div>
      )}
      
      {/* Playlist Info */}
      <div 
        className="flex-1 min-w-0 cursor-pointer"
        onClick={() => onPlay(playlist, playlistSongs)}
      >
        <div className="flex items-center gap-2">
          <ListMusic className="w-5 h-5 text-primary flex-shrink-0" />
          <h4 className="font-medium text-sm truncate">{playlist.name}</h4>
          <span className="text-xs text-muted-foreground">
            ({playlistSongs.length} {playlistSongs.length === 1 ? 'track' : 'tracks'})
          </span>
        </div>
      </div>
      
      {/* Action Buttons */}
      <div className="flex items-center gap-1">
        <Button
          size="icon"
          variant="ghost"
          className="h-8 w-8"
          onClick={() => onPlay(playlist, playlistSongs)}
          data-testid={`button-play-playlist-${playlist.id}`}
        >
          <Play className="w-4 h-4" />
        </Button>
        <Button
          size="icon"
          variant="ghost"
          className="h-8 w-8"
          onClick={onShare}
          data-testid={`button-share-playlist-${playlist.id}`}
        >
          <Share2 className="w-4 h-4" />
        </Button>
        <Button
          size="icon"
          variant="ghost"
          className="h-8 w-8"
          onClick={onEdit}
          data-testid={`button-edit-playlist-${playlist.id}`}
        >
          <Edit className="w-4 h-4" />
        </Button>
        <Button
          size="icon"
          variant="ghost"
          className="h-8 w-8 text-destructive hover:text-destructive"
          onClick={onDelete}
          data-testid={`button-delete-playlist-${playlist.id}`}
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

// Combined track type for My Tracks section
type MyTrack = {
  id: string;
  type: 'song' | 'custom';
  title: string;
  subtitle: string;
  isYouTube?: boolean;
  librarySortOrder: number;
  originalSong?: MeditationSong;
  originalCustom?: CustomMeditation;
};

// Sortable Track Row Component for My Tracks section
function SortableTrackRow({ 
  track, 
  onPlaySong,
  onPlayCustom,
  isMobile
}: { 
  track: MyTrack;
  onPlaySong: (song: MeditationSong) => void;
  onPlayCustom: (meditationId: string) => void;
  isMobile: boolean;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: track.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handlePlay = () => {
    if (track.type === 'song' && track.originalSong) {
      onPlaySong(track.originalSong);
    } else if (track.type === 'custom' && track.originalCustom) {
      onPlayCustom(track.originalCustom.id);
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center justify-between p-3 rounded-lg border hover:bg-accent/50 cursor-pointer transition-colors"
      onClick={handlePlay}
      data-testid={`track-item-${track.id}`}
    >
      <div className="flex items-center gap-3">
        {/* Drag Handle */}
        {!isMobile && (
          <div
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing touch-none"
            onClick={(e) => e.stopPropagation()}
            data-testid={`drag-handle-track-${track.id}`}
          >
            <GripVertical className="w-4 h-4 text-muted-foreground" />
          </div>
        )}
        {track.type === 'custom' ? (
          <Mic className="w-5 h-5 text-green-500" />
        ) : track.isYouTube ? (
          <Youtube className="w-5 h-5 text-red-500" />
        ) : (
          <Volume2 className="w-5 h-5 text-primary" />
        )}
        <div>
          <p className="font-medium text-sm">{track.title}</p>
          <p className="text-xs text-muted-foreground">
            {track.subtitle}
            {track.type === 'custom' && " • Custom Meditation"}
            {track.isYouTube && " • YouTube Video"}
          </p>
        </div>
      </div>
      <Button
        size="icon"
        variant="ghost"
        className="h-8 w-8"
        onClick={(e) => {
          e.stopPropagation();
          handlePlay();
        }}
        data-testid={`button-play-track-${track.id}`}
      >
        <Play className="w-4 h-4" />
      </Button>
    </div>
  );
}

// Sortable Library Item Component for DnD
function SortableLibraryItem({ 
  item, 
  onPlay,
  onToggleStar,
  onEdit,
  onCategoryChange,
  categories,
  isMobile
}: { 
  item: MeditationLibraryItem;
  onPlay: () => void;
  onToggleStar: () => void;
  onEdit: () => void;
  onCategoryChange: (categoryId: string | null) => void;
  categories: Array<{ id: string; name: string; color: string }>;
  isMobile: boolean;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: `${item.type}-${item.id}` });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div 
      ref={setNodeRef}
      style={{...style, pointerEvents: 'auto'}}
      className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
      data-testid={`library-item-${item.type}-${item.id}`}
      onClick={(e) => {
        console.log("Row clicked:", item.title);
      }}
    >
      <div className="flex items-center gap-3 flex-1 min-w-0">
        {/* Drag Handle */}
        {!isMobile && (
          <div
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing touch-none shrink-0"
            data-testid={`drag-handle-library-${item.id}`}
          >
            <GripVertical className="w-4 h-4 text-muted-foreground" />
          </div>
        )}
        
        {/* Type icon */}
        <div className="shrink-0">
          {item.type === 'song' ? (
            item.url && isYouTubeUrl(item.url) ? (
              <Youtube className="w-5 h-5 text-red-500" />
            ) : (
              <Music className="w-5 h-5 text-primary" />
            )
          ) : (
            <Mic className="w-5 h-5 text-purple-500" />
          )}
        </div>
        
        {/* Info */}
        <div className="min-w-0 flex-1">
          <p className="font-medium truncate">{item.title}</p>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            {item.artist && <span>{item.artist}</span>}
            {item.playlistName && (
              <span className="text-xs bg-muted px-1.5 py-0.5 rounded">
                {item.playlistName}
              </span>
            )}
            {item.subject && (
              <span className="text-xs text-primary">{item.subject}</span>
            )}
            {item.duration && (
              <span>{Math.floor(item.duration / 60)}:{String(item.duration % 60).padStart(2, '0')}</span>
            )}
          </div>
          {/* Tags and Category */}
          <div className="flex items-center gap-1 mt-1 flex-wrap">
            {item.tags && item.tags.length > 0 && item.tags.map((tag) => (
              <Badge key={tag} variant="outline" className="text-xs">
                {tag}
              </Badge>
            ))}
            {item.mnmCategoryId && categories.length > 0 && (
              <Badge 
                variant="secondary" 
                className="text-xs flex items-center gap-1"
                style={{ 
                  backgroundColor: `${categories.find(c => c.id === item.mnmCategoryId)?.color}20`,
                  borderColor: categories.find(c => c.id === item.mnmCategoryId)?.color
                }}
              >
                <div 
                  className="w-2 h-2 rounded-full" 
                  style={{ backgroundColor: categories.find(c => c.id === item.mnmCategoryId)?.color }}
                />
                {categories.find(c => c.id === item.mnmCategoryId)?.name || "Category"}
              </Badge>
            )}
          </div>
        </div>
      </div>
      
      {/* Actions */}
      <div className="flex items-center gap-2 shrink-0 ml-2" style={{pointerEvents: 'auto', position: 'relative', zIndex: 10}}>
        {/* Category Selector */}
        <Select
          value={item.mnmCategoryId || "none"}
          onValueChange={(value) => onCategoryChange(value === "none" ? null : value)}
        >
          <SelectTrigger className="h-8 w-8 p-0 border-none" onClick={(e) => e.stopPropagation()} data-testid={`select-category-${item.id}`}>
            <div 
              className="w-4 h-4 rounded-full border-2"
              style={{ 
                backgroundColor: item.mnmCategoryId 
                  ? categories.find(c => c.id === item.mnmCategoryId)?.color 
                  : 'transparent',
                borderColor: item.mnmCategoryId 
                  ? categories.find(c => c.id === item.mnmCategoryId)?.color 
                  : 'currentColor'
              }}
            />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">No Category</SelectItem>
            {categories.map((category) => (
              <SelectItem key={category.id} value={category.id}>
                <div className="flex items-center gap-2">
                  <div 
                    className="w-3 h-3 rounded-full" 
                    style={{ backgroundColor: category.color }}
                  />
                  {category.name}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          size="icon"
          variant="ghost"
          className="h-8 w-8"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            onToggleStar();
          }}
          data-testid={`button-star-${item.id}`}
        >
          <Star className={`w-4 h-4 ${item.isStarred ? 'fill-yellow-400 text-yellow-400' : ''}`} />
        </Button>
        <Button
          size="icon"
          variant="ghost"
          className="h-8 w-8 relative z-10"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            console.log("Edit button clicked for item:", item.id);
            onEdit();
          }}
          data-testid={`button-edit-library-${item.id}`}
        >
          <Edit className="w-4 h-4" />
        </Button>
        <button
          type="button"
          className="relative z-10 px-3 py-1 border rounded flex items-center gap-1 bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700"
          style={{pointerEvents: 'auto'}}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            console.log("Play button clicked for item:", item.id, item.title);
            alert("Play clicked: " + item.title);
            onPlay();
          }}
          data-testid={`button-play-${item.id}`}
        >
          <Play className="w-4 h-4" />
          Play
        </button>
      </div>
    </div>
  );
}

// Sortable Section wrapper for page layout reordering
interface SortableSectionProps {
  id: string;
  children: React.ReactNode;
  isMobile: boolean;
  order?: number;
}

function SortableSection({ id, children, isMobile, order = 0 }: SortableSectionProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    order,
  };

  return (
    <div ref={setNodeRef} style={style} className="relative group">
      {/* Drag handle - only show on desktop */}
      {!isMobile && (
        <div
          {...attributes}
          {...listeners}
          className="absolute -left-8 top-4 opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing z-10"
          data-testid={`section-drag-handle-${id}`}
        >
          <GripVertical className="w-5 h-5 text-muted-foreground" />
        </div>
      )}
      {children}
    </div>
  );
}

export default function MeditationPage() {
  const [, setLocation] = useLocation();
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const isMobile = useIsMobile();
  const [playMode, setPlayMode] = useState<"none" | "playlist" | "single" | "custom" | "youtube">("none");
  const [selectedMeditation, setSelectedMeditation] = useState<string | null>(null);
  const [currentYouTubeVideoId, setCurrentYouTubeVideoId] = useState<string | null>(null);
  const [currentYouTubeSong, setCurrentYouTubeSong] = useState<{ title: string; artist?: string } | null>(null);
  const [currentSingleSong, setCurrentSingleSong] = useState<{ title: string; artist?: string } | null>(null);
  
  // Ref to YouTube iframe for API control
  const youtubeIframeRef = useRef<HTMLIFrameElement>(null);
  
  // YouTube embed error state - tracks when video embedding is blocked
  const [youtubeEmbedError, setYoutubeEmbedError] = useState(false);
  
  // Stats filter state
  const [statsFilter, setStatsFilter] = useState<"day" | "week" | "month" | "custom">("day");
  const [customStartDate, setCustomStartDate] = useState<Date | undefined>(undefined);
  const [customEndDate, setCustomEndDate] = useState<Date | undefined>(undefined);
  
  // Session tracking
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const sessionStartTimeRef = useRef<Date | null>(null);
  
  // Playlist customization state
  const [showPlaylistDialog, setShowPlaylistDialog] = useState(false);
  const [newPlaylistName, setNewPlaylistName] = useState("");
  const [selectedPlaylistId, setSelectedPlaylistId] = useState<string | null>(null);
  const [currentPlayingPlaylist, setCurrentPlayingPlaylist] = useState<MeditationPlaylist | null>(null);
  
  // Editing playlist state
  const [editingPlaylist, setEditingPlaylist] = useState<MeditationPlaylist | null>(null);
  const [editPlaylistName, setEditPlaylistName] = useState("");

  // Sharing playlist state
  const [sharingPlaylist, setSharingPlaylist] = useState<MeditationPlaylist | null>(null);
  const [selectedFriendId, setSelectedFriendId] = useState<string>("");

  // Section ordering for page layout (only sections in playMode === "none" are reorderable)
  type SectionId = 'quick-actions' | 'playlists' | 'custom-meditations' | 'tracks' | 'stats' | 'goal';
  const defaultSectionOrder: SectionId[] = ['quick-actions', 'playlists', 'custom-meditations', 'tracks', 'stats', 'goal'];
  
  const [sectionOrder, setSectionOrder] = useState<SectionId[]>(defaultSectionOrder);

  // Load from user-scoped key once userId is known; clean up legacy unscoped key.
  useEffect(() => {
    try {
      const saved = localStorage.getItem(userLsKey('meditation-section-order', userId));
      if (saved) {
        const parsed = JSON.parse(saved);
        const validSections = parsed.filter((s: string) => defaultSectionOrder.includes(s as SectionId));
        const missingSections = defaultSectionOrder.filter(s => !validSections.includes(s));
        setSectionOrder([...validSections, ...missingSections] as SectionId[]);
      }
      if (userId) localStorage.removeItem('meditation-section-order');
    } catch {}
  }, [userId]);

  // Save section order to user-scoped localStorage key when it changes.
  useEffect(() => {
    localStorage.setItem(userLsKey('meditation-section-order', userId), JSON.stringify(sectionOrder));
  }, [sectionOrder, userId]);

  // Handle section drag end
  const handleSectionDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    
    if (!over || active.id === over.id) return;

    const oldIndex = sectionOrder.indexOf(active.id as SectionId);
    const newIndex = sectionOrder.indexOf(over.id as SectionId);

    if (oldIndex !== -1 && newIndex !== -1) {
      setSectionOrder(arrayMove(sectionOrder, oldIndex, newIndex));
    }
  };

  // DnD sensors for playlist reordering
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const { state: playerState, controls: playerControls } = useAudioPlayer();
  const { state: mixerState, controls: mixerControls } = useAudioMixer();
  const { state: ttsState, controls: ttsControls } = useTextToSpeech();

  const { data: playlists = [] } = useQuery<MeditationPlaylist[]>({
    queryKey: ["/api/meditation/playlists"],
  });

  const userPlaylist = playlists[0];

  const { data: songs = [] } = useQuery<MeditationSong[]>({
    queryKey: ["/api/meditation/playlists", userPlaylist?.id, "songs"],
    enabled: !!userPlaylist?.id,
  });

  // Sort songs by librarySortOrder for display
  const sortedSongs = [...songs].sort((a, b) => (a.librarySortOrder || 0) - (b.librarySortOrder || 0));

  // Mutation to reorder songs
  const reorderSongsMutation = useMutation({
    mutationFn: async (orderedIds: string[]) => {
      return apiRequest("PATCH", "/api/meditation/songs/reorder", { orderedIds });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/meditation/playlists", userPlaylist?.id, "songs"] });
    },
  });

  // Mutation to reorder mixed tracks (songs and custom meditations)
  const reorderMyTracksMutation = useMutation({
    mutationFn: async (tracks: { id: string; type: 'song' | 'custom' }[]) => {
      return apiRequest("PATCH", "/api/meditation/my-tracks/reorder", { tracks });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/meditation/playlists", userPlaylist?.id, "songs"] });
      queryClient.invalidateQueries({ queryKey: ["/api/meditation/custom"] });
    },
  });

  // Handle drag end for tracks reordering
  const handleTracksDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = myTracks.findIndex(t => t.id === active.id);
    const newIndex = myTracks.findIndex(t => t.id === over.id);

    if (oldIndex !== -1 && newIndex !== -1) {
      const newOrder = arrayMove(myTracks, oldIndex, newIndex);
      reorderMyTracksMutation.mutate(newOrder.map(t => ({ id: t.id, type: t.type })));
    }
  };

  const { data: customMeditations = [] } = useQuery<CustomMeditation[]>({
    queryKey: ["/api/meditation/custom"],
  });

  // Combine songs and custom meditations into a unified "My Tracks" list
  const myTracks: MyTrack[] = [
    ...songs.map((song): MyTrack => ({
      id: song.id,
      type: 'song',
      title: song.title,
      subtitle: song.artist || "Unknown Artist",
      isYouTube: song.url?.includes('youtube.com') || song.url?.includes('youtu.be'),
      librarySortOrder: song.librarySortOrder || 0,
      originalSong: song,
    })),
    ...customMeditations.map((meditation): MyTrack => ({
      id: meditation.id,
      type: 'custom',
      title: meditation.name,
      subtitle: `${meditation.voiceType === 'recorded' ? 'Recorded' : meditation.voiceType} voice`,
      librarySortOrder: meditation.librarySortOrder || 0,
      originalCustom: meditation,
    })),
  ].sort((a, b) => a.librarySortOrder - b.librarySortOrder);

  // Query all songs from all playlists for the add media dialog
  const { data: allSongsFromAllPlaylists = [] } = useQuery<MeditationSong[]>({
    queryKey: ["/api/meditation/all-songs"],
  });

  // Mutation to move a song to a different playlist
  const moveSongToPlaylistMutation = useMutation({
    mutationFn: async ({ songId, newPlaylistId }: { songId: string; newPlaylistId: string }) => {
      return apiRequest("PATCH", `/api/meditation/songs/${songId}`, { playlistId: newPlaylistId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/meditation/all-songs"] });
      queryClient.invalidateQueries({ queryKey: ["/api/meditation/playlists"] });
      playlists.forEach(p => {
        queryClient.invalidateQueries({ queryKey: ["/api/meditation/playlists", p.id, "songs"] });
      });
      toast({ title: "Media moved to playlist" });
    },
  });

  // Mutation to delete a song from a playlist
  const deleteSongMutation = useMutation({
    mutationFn: async (songId: string) => {
      return apiRequest("DELETE", `/api/meditation/songs/${songId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/meditation/all-songs"] });
      queryClient.invalidateQueries({ queryKey: ["/api/meditation/playlists"] });
      playlists.forEach(p => {
        queryClient.invalidateQueries({ queryKey: ["/api/meditation/playlists", p.id, "songs"] });
      });
      toast({ title: "Media removed from playlist" });
    },
  });

  // Toast hook
  const { toast } = useToast();

  // Friends query for sharing
  interface FriendWithUser extends Friendship {
    friend: {
      id: string;
      email: string | null;
      username: string | null;
      firstName: string | null;
      lastName: string | null;
      profileImageUrl: string | null;
    };
  }
  const { data: friends = [] } = useQuery<FriendWithUser[]>({
    queryKey: ["/api/friends"],
  });

  // Shared playlists query (playlists shared with me)
  interface SharedPlaylistWithDetails extends PlaylistShare {
    playlist: MeditationPlaylist;
    owner: {
      id: string;
      email: string | null;
      username: string | null;
      firstName: string | null;
      lastName: string | null;
      profileImageUrl: string | null;
    };
    songs: MeditationSong[];
  }
  const { data: sharedPlaylists = [] } = useQuery<SharedPlaylistWithDetails[]>({
    queryKey: ["/api/meditation/playlists/shared-with-me"],
  });

  // Share playlist mutation
  const sharePlaylistMutation = useMutation({
    mutationFn: async ({ playlistId, friendId }: { playlistId: string; friendId: string }) => {
      return apiRequest("POST", `/api/meditation/playlists/${playlistId}/share`, { friendId });
    },
    onSuccess: () => {
      toast({ title: "Playlist shared!", description: "Your friend can now access this playlist." });
      queryClient.invalidateQueries({ queryKey: ["/api/meditation/playlists/shared-with-me"] });
      setSharingPlaylist(null);
      setSelectedFriendId("");
    },
    onError: () => {
      toast({ title: "Failed to share", description: "Could not share the playlist. Please try again.", variant: "destructive" });
    },
  });

  // Fetch user preferences for meditation goals
  const { data: userPreferences } = useQuery<UserPreferences>({
    queryKey: ["/api/user/preferences"],
  });

  // Goal state - initialize from preferences
  const [goalMinutes, setGoalMinutes] = useState<number | undefined>(undefined);
  const [goalPeriod, setGoalPeriod] = useState<"week" | "month" | "custom">("week");
  const [goalStartDate, setGoalStartDate] = useState<Date | undefined>(undefined);
  const [goalEndDate, setGoalEndDate] = useState<Date | undefined>(undefined);

  // Sync goal state with preferences when loaded
  useEffect(() => {
    if (userPreferences) {
      if (userPreferences.meditationGoalMinutes) {
        setGoalMinutes(userPreferences.meditationGoalMinutes);
      }
      if (userPreferences.meditationGoalPeriod) {
        setGoalPeriod(userPreferences.meditationGoalPeriod as "week" | "month" | "custom");
      }
      if (userPreferences.meditationGoalStartDate) {
        setGoalStartDate(new Date(userPreferences.meditationGoalStartDate));
      }
      if (userPreferences.meditationGoalEndDate) {
        setGoalEndDate(new Date(userPreferences.meditationGoalEndDate));
      }
    }
  }, [userPreferences]);

  // Calculate goal date range based on period
  const getGoalDateRange = () => {
    const now = new Date();
    let start: Date;
    let end: Date;
    
    switch (goalPeriod) {
      case "week":
        start = startOfWeek(now, { weekStartsOn: 1 });
        end = endOfDay(now);
        break;
      case "month":
        start = startOfMonth(now);
        end = endOfDay(now);
        break;
      case "custom":
        start = goalStartDate ? startOfDay(goalStartDate) : startOfDay(now);
        end = goalEndDate ? endOfDay(goalEndDate) : endOfDay(now);
        break;
    }
    
    return { start, end };
  };

  // Fetch meditation stats for goal period
  const { data: goalStats } = useQuery<{ totalSeconds: number; sessionCount: number }>({
    queryKey: ["/api/meditation/stats", "goal", goalPeriod, goalStartDate?.toISOString(), goalEndDate?.toISOString()],
    queryFn: async () => {
      const { start, end } = getGoalDateRange();
      const response = await fetch(
        `/api/meditation/stats?startDate=${start.toISOString()}&endDate=${end.toISOString()}`,
        { credentials: "include" }
      );
      if (!response.ok) throw new Error("Failed to fetch goal stats");
      return response.json();
    },
    enabled: !!goalMinutes,
  });

  // Mutation to save goal settings
  const saveGoalSettingsMutation = useMutation({
    mutationFn: async (data: { 
      meditationGoalMinutes?: number;
      meditationGoalPeriod?: string;
      meditationGoalStartDate?: string | null;
      meditationGoalEndDate?: string | null;
    }) => {
      const response = await apiRequest("PATCH", "/api/user/preferences", data);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/user/preferences"] });
    },
  });

  // Calculate goal score (minutes completed vs goal)
  const goalMinutesCompleted = goalStats ? Math.floor(goalStats.totalSeconds / 60) : 0;
  const goalScore = goalMinutes ? goalMinutesCompleted - goalMinutes : 0;

  // Create playlist mutation
  const createPlaylistMutation = useMutation({
    mutationFn: async (data: { name: string }) => {
      const response = await apiRequest("POST", "/api/meditation/playlists", data);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/meditation/playlists"] });
      setNewPlaylistName("");
    },
  });

  // Delete playlist mutation
  const deletePlaylistMutation = useMutation({
    mutationFn: async (playlistId: string) => {
      return apiRequest("DELETE", `/api/meditation/playlists/${playlistId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/meditation/playlists"] });
    },
  });

  // Update playlist mutation
  const updatePlaylistMutation = useMutation({
    mutationFn: async ({ id, name }: { id: string; name: string }) => {
      return apiRequest("PATCH", `/api/meditation/playlists/${id}`, { name });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/meditation/playlists"] });
      setEditingPlaylist(null);
      setEditPlaylistName("");
    },
  });

  // Reorder playlists mutation
  const reorderPlaylistsMutation = useMutation({
    mutationFn: async (orderedIds: string[]) => {
      return apiRequest("PATCH", "/api/meditation/playlists/reorder", { orderedIds });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/meditation/playlists"] });
    },
  });

  // Handle playlist drag end
  const handlePlaylistDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    
    if (over && active.id !== over.id) {
      const oldIndex = playlists.findIndex((p) => p.id === active.id);
      const newIndex = playlists.findIndex((p) => p.id === over.id);
      
      const newOrder = arrayMove(playlists, oldIndex, newIndex);
      const orderedIds = newOrder.map(p => p.id);
      
      // Optimistically update
      queryClient.setQueryData(["/api/meditation/playlists"], newOrder);
      
      // Save to server
      reorderPlaylistsMutation.mutate(orderedIds);
    }
  };

  // Fetch songs for a specific playlist
  const { data: selectedPlaylistSongs = [] } = useQuery<MeditationSong[]>({
    queryKey: ["/api/meditation/playlists", selectedPlaylistId, "songs"],
    enabled: !!selectedPlaylistId,
  });

  // Handle playing a specific playlist
  const handlePlaySpecificPlaylist = async (playlist: MeditationPlaylist, playlistSongs: MeditationSong[]) => {
    const audioSongs = playlistSongs.filter(song => !isYouTubeUrl(song.url));
    
    if (audioSongs.length === 0) {
      // If all songs are YouTube, play the first YouTube song in embedded player
      const youtubeSong = playlistSongs.find(song => isYouTubeUrl(song.url));
      if (youtubeSong) {
        const videoId = extractYouTubeVideoId(youtubeSong.url);
        if (videoId) {
          setCurrentYouTubeVideoId(videoId);
          setCurrentYouTubeSong({ title: youtubeSong.title, artist: youtubeSong.artist || undefined });
          setPlayMode("youtube");
          setCurrentPlayingPlaylist(playlist);
          createSessionMutation.mutate({ type: "playlist", sourceId: playlist.id, sourceName: playlist.name });
        }
      }
      return;
    }

    stopCurrentPlayback();

    const playlistData = audioSongs.map(song => ({
      id: song.id,
      title: song.title,
      artist: song.artist || undefined,
      url: song.url,
      duration: song.duration || undefined,
    }));

    playerControls.loadPlaylist(playlistData, 0);
    playerControls.play();
    setPlayMode("playlist");
    setCurrentPlayingPlaylist(playlist);
    createSessionMutation.mutate({ type: "playlist", sourceId: playlist.id, sourceName: playlist.name });
  };

  // Library filter state
  const [librarySearchQuery, setLibrarySearchQuery] = useState("");
  const [libraryStarredOnly, setLibraryStarredOnly] = useState(false);
  const [librarySelectedTags, setLibrarySelectedTags] = useState<string[]>([]);
  const [librarySelectedCategoryId, setLibrarySelectedCategoryId] = useState<string | null>(null);
  
  // MnM Categories query
  const { data: mnmCategories = [] } = useQuery<MnmCategory[]>({
    queryKey: ["/api/mnm/categories"],
  });

  // MnM Category management state
  const [newCategoryName, setNewCategoryName] = useState("");
  const [categoryPopoverOpen, setCategoryPopoverOpen] = useState(false);

  // Create MnM category mutation
  const createCategoryMutation = useMutation({
    mutationFn: async (name: string) => {
      const response = await apiRequest("POST", "/api/mnm/categories", { name });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/mnm/categories"] });
      setNewCategoryName("");
    },
  });

  // Delete MnM category mutation
  const deleteCategoryMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/mnm/categories/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/mnm/categories"] });
      queryClient.invalidateQueries({ queryKey: ["/api/meditation/library"] });
      if (librarySelectedCategoryId) setLibrarySelectedCategoryId(null);
    },
  });

  // Library item editing state
  const [editingLibraryItem, setEditingLibraryItem] = useState<MeditationLibraryItem | null>(null);
  const [editFormData, setEditFormData] = useState({
    title: "",
    artist: "",
    subject: "",
    tags: [] as string[],
    contentType: "meditation",
    newTag: "",
    url: "" as string | undefined,
    duration: undefined as number | undefined,
    playlistId: undefined as string | undefined,
    mnmCategoryId: undefined as string | undefined,
    isStarred: false,
  });

  // Initialize edit form when item is selected
  useEffect(() => {
    if (editingLibraryItem) {
      setEditFormData({
        title: editingLibraryItem.title || "",
        artist: editingLibraryItem.artist || "",
        subject: editingLibraryItem.subject || "",
        tags: editingLibraryItem.tags || [],
        contentType: editingLibraryItem.contentType || "meditation",
        newTag: "",
        url: editingLibraryItem.url || "",
        duration: editingLibraryItem.duration || undefined,
        playlistId: editingLibraryItem.playlistId || undefined,
        mnmCategoryId: editingLibraryItem.mnmCategoryId || undefined,
        isStarred: editingLibraryItem.isStarred || false,
      });
    }
  }, [editingLibraryItem]);

  // Mutation to update library item
  const updateLibraryItemMutation = useMutation({
    mutationFn: async ({ id, type, updates }: { id: string; type: 'song' | 'custom'; updates: any }) => {
      const endpoint = type === 'song' ? `/api/meditation/songs/${id}` : `/api/meditation/custom/${id}`;
      const response = await apiRequest("PATCH", endpoint, updates);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/meditation/library"] });
      queryClient.invalidateQueries({ queryKey: ["/api/meditation/playlists"] });
      queryClient.invalidateQueries({ queryKey: ["/api/meditation/custom"] });
      setEditingLibraryItem(null);
    },
  });

  const handleSaveLibraryItem = () => {
    if (!editingLibraryItem) return;
    
    const updates: any = {
      subject: editFormData.subject || null,
      tags: editFormData.tags,
      contentType: editFormData.contentType,
      isStarred: editFormData.isStarred,
      mnmCategoryId: editFormData.mnmCategoryId || null,
    };
    
    // For songs, also allow editing title, artist, url, duration, and playlist
    if (editingLibraryItem.type === 'song') {
      updates.title = editFormData.title;
      updates.artist = editFormData.artist || null;
      updates.url = editFormData.url || null;
      updates.duration = editFormData.duration || null;
      updates.playlistId = editFormData.playlistId || null;
    } else {
      // For custom meditations, update name instead of title
      updates.name = editFormData.title;
    }
    
    updateLibraryItemMutation.mutate({
      id: editingLibraryItem.id,
      type: editingLibraryItem.type,
      updates,
    });
  };

  // Build library query params
  const libraryQueryParams = new URLSearchParams();
  if (librarySearchQuery) libraryQueryParams.set("searchQuery", librarySearchQuery);
  if (libraryStarredOnly) libraryQueryParams.set("starredOnly", "true");
  if (librarySelectedTags.length > 0) libraryQueryParams.set("tags", librarySelectedTags.join(","));
  if (librarySelectedCategoryId) libraryQueryParams.set("mnmCategoryId", librarySelectedCategoryId);
  
  const { data: libraryItems = [], isLoading: libraryLoading } = useQuery<MeditationLibraryItem[]>({
    queryKey: ["/api/meditation/library", librarySearchQuery, libraryStarredOnly, librarySelectedTags, librarySelectedCategoryId],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (librarySearchQuery) params.set("searchQuery", librarySearchQuery);
      if (libraryStarredOnly) params.set("starredOnly", "true");
      if (librarySelectedTags.length > 0) params.set("tags", librarySelectedTags.join(","));
      if (librarySelectedCategoryId) params.set("mnmCategoryId", librarySelectedCategoryId);
      const response = await fetch(`/api/meditation/library?${params.toString()}`, { credentials: "include" });
      if (!response.ok) throw new Error("Failed to fetch library");
      return response.json();
    },
  });

  // Get all unique tags from library items
  const allTags = [...new Set(libraryItems.flatMap(item => item.tags || []))].sort();

  // Mutation to toggle star status
  const toggleStarMutation = useMutation({
    mutationFn: async ({ id, type, isStarred }: { id: string; type: 'song' | 'custom'; isStarred: boolean }) => {
      const endpoint = type === 'song' ? `/api/meditation/songs/${id}` : `/api/meditation/custom/${id}`;
      const response = await apiRequest("PATCH", endpoint, { isStarred });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/meditation/library"] });
      queryClient.invalidateQueries({ queryKey: ["/api/meditation/playlists"] });
      queryClient.invalidateQueries({ queryKey: ["/api/meditation/custom"] });
    },
  });

  // Mutation to update subject
  const updateSubjectMutation = useMutation({
    mutationFn: async ({ id, type, subject }: { id: string; type: 'song' | 'custom'; subject: string }) => {
      const endpoint = type === 'song' ? `/api/meditation/songs/${id}` : `/api/meditation/custom/${id}`;
      const response = await apiRequest("PATCH", endpoint, { subject });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/meditation/library"] });
    },
  });

  // Mutation to update tags
  const updateTagsMutation = useMutation({
    mutationFn: async ({ id, type, tags }: { id: string; type: 'song' | 'custom'; tags: string[] }) => {
      const endpoint = type === 'song' ? `/api/meditation/songs/${id}` : `/api/meditation/custom/${id}`;
      const response = await apiRequest("PATCH", endpoint, { tags });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/meditation/library"] });
    },
  });

  // Mutation to update MnM category
  const updateMnmCategoryMutation = useMutation({
    mutationFn: async ({ id, type, mnmCategoryId }: { id: string; type: 'song' | 'custom'; mnmCategoryId: string | null }) => {
      const endpoint = type === 'song' ? `/api/meditation/songs/${id}` : `/api/meditation/custom/${id}`;
      const response = await apiRequest("PATCH", endpoint, { mnmCategoryId });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/meditation/library"] });
    },
  });

  // Mutation to reorder library items
  const reorderLibraryMutation = useMutation({
    mutationFn: async (items: Array<{ id: string; type: 'song' | 'custom'; librarySortOrder: number }>) => {
      const response = await apiRequest("POST", "/api/meditation/library/reorder", { items });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/meditation/library"] });
    },
  });

  // Handle library drag end
  const handleLibraryDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    
    if (!over || active.id === over.id) return;

    const oldIndex = libraryItems.findIndex(item => `${item.type}-${item.id}` === active.id);
    const newIndex = libraryItems.findIndex(item => `${item.type}-${item.id}` === over.id);

    if (oldIndex !== -1 && newIndex !== -1) {
      const newItems = arrayMove(libraryItems, oldIndex, newIndex);
      
      // Update sort orders
      const itemsToUpdate = newItems.map((item, index) => ({
        id: item.id,
        type: item.type,
        librarySortOrder: index,
      }));

      reorderLibraryMutation.mutate(itemsToUpdate);
    }
  };

  // Calculate date range for stats
  const getStatsDateRange = () => {
    const now = new Date();
    let start: Date;
    let end: Date;
    
    switch (statsFilter) {
      case "day":
        start = startOfDay(now);
        end = endOfDay(now);
        break;
      case "week":
        start = startOfWeek(now, { weekStartsOn: 1 });
        end = endOfDay(now);
        break;
      case "month":
        start = startOfMonth(now);
        end = endOfDay(now);
        break;
      case "custom":
        start = customStartDate ? startOfDay(customStartDate) : startOfDay(now);
        end = customEndDate ? endOfDay(customEndDate) : endOfDay(now);
        break;
    }
    
    return { start, end };
  };

  const { start: statsStartDate, end: statsEndDate } = getStatsDateRange();

  // Fetch meditation stats
  const { data: meditationStats } = useQuery<{ totalSeconds: number; sessionCount: number }>({
    queryKey: ["/api/meditation/stats", statsFilter, customStartDate?.toISOString(), customEndDate?.toISOString()],
    queryFn: async () => {
      const { start, end } = getStatsDateRange();
      const response = await fetch(
        `/api/meditation/stats?startDate=${start.toISOString()}&endDate=${end.toISOString()}`,
        { credentials: "include" }
      );
      if (!response.ok) throw new Error("Failed to fetch meditation stats");
      return response.json();
    },
  });

  // Create session mutation
  const createSessionMutation = useMutation({
    mutationFn: async (data: { type: string; sourceId?: string; sourceName?: string }) => {
      const response = await apiRequest("POST", "/api/meditation/sessions", {
        type: data.type,
        sourceId: data.sourceId,
        sourceName: data.sourceName,
        startedAt: new Date().toISOString(),
      });
      return response.json();
    },
    onSuccess: (session: MeditationSession) => {
      setCurrentSessionId(session.id);
      sessionStartTimeRef.current = new Date();
    },
  });

  // Update session mutation (to record end time and duration)
  const updateSessionMutation = useMutation({
    mutationFn: async (data: { id: string; durationSeconds: number }) => {
      const response = await apiRequest("PATCH", `/api/meditation/sessions/${data.id}`, {
        endedAt: new Date().toISOString(),
        durationSeconds: data.durationSeconds,
      });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/meditation/stats"] });
      setCurrentSessionId(null);
      sessionStartTimeRef.current = null;
    },
  });

  // End current session
  const endCurrentSession = () => {
    if (currentSessionId && sessionStartTimeRef.current) {
      const durationSeconds = Math.floor(
        (new Date().getTime() - sessionStartTimeRef.current.getTime()) / 1000
      );
      updateSessionMutation.mutate({ id: currentSessionId, durationSeconds });
    }
  };

  // Cleanup on unmount - end any active session
  useEffect(() => {
    return () => {
      if (currentSessionId && sessionStartTimeRef.current) {
        const durationSeconds = Math.floor(
          (new Date().getTime() - sessionStartTimeRef.current.getTime()) / 1000
        );
        // Use navigator.sendBeacon for reliable cleanup on unmount
        const data = JSON.stringify({
          endedAt: new Date().toISOString(),
          durationSeconds,
        });
        navigator.sendBeacon(`/api/meditation/sessions/${currentSessionId}`, new Blob([data], { type: 'application/json' }));
      }
    };
  }, [currentSessionId]);

  // Handle audio player finishing naturally
  useEffect(() => {
    if (playerState.isFinished && (playMode === "playlist" || playMode === "single")) {
      endCurrentSession();
      setPlayMode("none");
    }
  }, [playerState.isFinished, playMode]);

  // Handle TTS finishing naturally: only end the session on a speaking -> not-speaking transition
  const wasSpeakingRef = useRef(false);
  useEffect(() => {
    const wasSpeaking = wasSpeakingRef.current;
    wasSpeakingRef.current = ttsState.isSpeaking;

    if (wasSpeaking && !ttsState.isSpeaking && playMode === "custom" && selectedMeditation) {
      if (!mixerState.isPlaying && sessionStartTimeRef.current) {
        endCurrentSession();
        setPlayMode("none");
        setSelectedMeditation(null);
      }
    }
  }, [ttsState.isSpeaking, mixerState.isPlaying, playMode, selectedMeditation]);

  // Handle recorded-voice and music-only meditations finishing naturally via the audio mixer:
  // only end the session on a playing -> not-playing transition where the mixer
  // reports a natural finish (not a manual stop/pause/seek)
  const wasMixerPlayingRef = useRef(false);
  useEffect(() => {
    const wasMixerPlaying = wasMixerPlayingRef.current;
    wasMixerPlayingRef.current = mixerState.isPlaying;

    if (wasMixerPlaying && !mixerState.isPlaying && mixerState.isFinished && playMode === "custom" && selectedMeditation) {
      if (!ttsState.isSpeaking && sessionStartTimeRef.current) {
        endCurrentSession();
        setPlayMode("none");
        setSelectedMeditation(null);
      }
    }
  }, [mixerState.isPlaying, mixerState.isFinished, ttsState.isSpeaking, playMode, selectedMeditation]);

  // YouTube IFrame API error detection - listen for error messages and check for loading issues
  useEffect(() => {
    if (!currentYouTubeVideoId || playMode !== "youtube") return;

    let errorCheckTimeout: NodeJS.Timeout | null = null;
    let hasReceivedPlaybackState = false;

    const handleMessage = (event: MessageEvent) => {
      // Only handle messages from YouTube domains
      if (!event.origin.includes('youtube.com') && !event.origin.includes('youtube-nocookie.com')) return;
      
      try {
        // YouTube IFrame API sends JSON messages with event data
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        
        // Check for error events from YouTube player
        // Error codes: 2=invalid param, 5=HTML5 error, 100=not found, 101/150=embed disabled
        if (data.event === 'onError' || (data.info && data.info.error)) {
          const errorCode = data.info?.error || data.data;
          console.log("YouTube embed error detected:", errorCode);
          
          // Set error state for codes that indicate playback won't work
          if ([2, 5, 100, 101, 150].includes(errorCode)) {
            setYoutubeEmbedError(true);
          }
        }
        
        // Track if we've received any playback state (indicates video is working)
        if (data.event === 'onStateChange' && typeof data.info === 'number') {
          hasReceivedPlaybackState = true;
          // State 1 = playing, 2 = paused, 3 = buffering - all indicate video is working
          if ([1, 2, 3].includes(data.info)) {
            // Video is working, clear any pending error check
            if (errorCheckTimeout) {
              clearTimeout(errorCheckTimeout);
              errorCheckTimeout = null;
            }
          }
        }
        
        // onReady event - video iframe loaded
        if (data.event === 'onReady') {
          hasReceivedPlaybackState = true;
        }
      } catch {
        // Non-JSON messages from iframe, ignore
      }
    };

    window.addEventListener('message', handleMessage);
    
    // Set a timeout - if we don't receive any playback events after 8 seconds, show fallback
    errorCheckTimeout = setTimeout(() => {
      if (!hasReceivedPlaybackState) {
        console.log("YouTube video may be unavailable - no playback state received after timeout");
        // Don't automatically show error, but we could if needed
        // setYoutubeEmbedError(true);
      }
    }, 8000);
    
    return () => {
      window.removeEventListener('message', handleMessage);
      if (errorCheckTimeout) {
        clearTimeout(errorCheckTimeout);
      }
    };
  }, [currentYouTubeVideoId, playMode]);

  // Format duration for display
  const formatDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes} min`;
  };

  // Stop YouTube playback using iframe postMessage API
  const stopYouTubePlayback = () => {
    if (youtubeIframeRef.current && youtubeIframeRef.current.contentWindow) {
      try {
        // Send stop command using YouTube IFrame API
        youtubeIframeRef.current.contentWindow.postMessage(
          JSON.stringify({ event: 'command', func: 'stopVideo', args: [] }),
          'https://www.youtube-nocookie.com'
        );
      } catch {
        // Ignore errors if iframe is not accessible
      }
    }
  };

  // Stop any existing playback before starting new
  const stopCurrentPlayback = (shouldEndSession = true) => {
    // Stop YouTube first using API before clearing state
    stopYouTubePlayback();
    
    playerControls.stop();
    mixerControls.stop();
    ttsControls.stop();
    setCurrentYouTubeVideoId(null);
    setCurrentYouTubeSong(null);
    setCurrentSingleSong(null);
    setSelectedMeditation(null);
    
    // End the current session if one exists
    if (shouldEndSession) {
      endCurrentSession();
    }
  };

  // Play a specific song (YouTube or regular audio)
  const handlePlaySong = (song: MeditationSong) => {
    // Stop any existing playback first
    stopCurrentPlayback();
    
    if (isYouTubeUrl(song.url)) {
      const videoId = extractYouTubeVideoId(song.url);
      if (videoId) {
        // Reset embed error state when playing new video
        setYoutubeEmbedError(false);
        setCurrentYouTubeVideoId(videoId);
        setCurrentYouTubeSong({ title: song.title, artist: song.artist || undefined });
        setPlayMode("youtube");
        // Start session tracking for YouTube
        createSessionMutation.mutate({ type: "youtube", sourceId: song.id, sourceName: song.title });
      }
    } else {
      // Regular audio - use the audio player in single mode
      playerControls.loadPlaylist([{
        id: song.id,
        title: song.title,
        artist: song.artist || undefined,
        url: song.url,
        duration: song.duration || undefined,
      }], 0);
      playerControls.play();
      setCurrentSingleSong({ title: song.title, artist: song.artist || undefined });
      setPlayMode("single");
      // Start session tracking for single song
      createSessionMutation.mutate({ type: "single", sourceId: song.id, sourceName: song.title });
    }
  };

  const handlePlayPlaylist = () => {
    if (songs.length === 0) {
      setLocation("/meditation/settings");
      return;
    }

    // Filter out YouTube songs for regular playlist playback
    const audioSongs = songs.filter(song => !isYouTubeUrl(song.url));
    
    if (audioSongs.length === 0) {
      // All songs are YouTube - play the first one
      const firstSong = songs[0];
      if (firstSong) {
        handlePlaySong(firstSong);
      }
      return;
    }

    // Stop any existing playback first
    stopCurrentPlayback();

    const playlist = audioSongs.map(song => ({
      id: song.id,
      title: song.title,
      artist: song.artist || undefined,
      url: song.url,
      duration: song.duration || undefined,
    }));

    playerControls.loadPlaylist(playlist, 0);
    playerControls.play();
    setPlayMode("playlist");
    // Start session tracking for playlist
    createSessionMutation.mutate({ type: "playlist", sourceId: userPlaylist?.id, sourceName: userPlaylist?.name || "Playlist" });
  };

  const handlePlayCustomMeditation = async (meditationId: string) => {
    const meditation = customMeditations.find(m => m.id === meditationId);
    if (!meditation) return;

    // Stop any existing playback first
    stopCurrentPlayback();

    setSelectedMeditation(meditationId);
    setPlayMode("custom");
    // Start session tracking for custom meditation
    createSessionMutation.mutate({ type: "custom", sourceId: meditationId, sourceName: meditation.name });

    const backgroundMusic = meditation.backgroundMusicId 
      ? songs.find(s => s.id === meditation.backgroundMusicId)
      : null;

    mixerControls.setMusicVolume(meditation.musicVolume);
    mixerControls.setVoiceVolume(meditation.voiceVolume);

    if (meditation.voiceType === "recorded" && meditation.voiceAudioUrl) {
      const voiceBlob = base64ToBlob(meditation.voiceAudioUrl);
      await mixerControls.loadAndPlay(
        backgroundMusic ? { type: 'music', url: backgroundMusic.url } : undefined,
        { type: 'voice', blob: voiceBlob }
      );
    } else if (meditation.voiceType === "male" || meditation.voiceType === "female") {
      const voices = ttsControls.getVoicesByGender(meditation.voiceType);
      if (voices.length > 0 && voices[0]) {
        ttsControls.setVoice(voices[0]);
      }
      ttsControls.speak(meditation.scriptText);
    }
  };

  const handleStop = () => {
    if (playMode === "playlist" || playMode === "single") {
      playerControls.stop();
      setCurrentSingleSong(null);
    } else if (playMode === "custom") {
      mixerControls.stop();
      ttsControls.stop();
    } else if (playMode === "youtube") {
      // Explicitly stop YouTube playback via API before clearing state
      stopYouTubePlayback();
      setCurrentYouTubeVideoId(null);
      setCurrentYouTubeSong(null);
    }
    setPlayMode("none");
    setSelectedMeditation(null);
    // End the session
    endCurrentSession();
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const currentMeditation = selectedMeditation 
    ? customMeditations.find(m => m.id === selectedMeditation)
    : null;

  // YouTube connection status
  const { data: youtubeConnection, refetch: refetchYoutubeConnection } = useQuery<{
    connected: boolean;
    email?: string;
    channelName?: string;
    channelId?: string;
  }>({
    queryKey: ["/api/youtube/connection"],
  });

  const disconnectYoutubeMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("DELETE", "/api/youtube/disconnect");
    },
    onSuccess: () => {
      refetchYoutubeConnection();
      toast({ title: "YouTube Disconnected", description: "Your YouTube account has been disconnected." });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to disconnect YouTube.", variant: "destructive" });
    },
  });

  return (
    <div className="min-h-screen bg-background">
      <div className="bg-card border-b border-border px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-foreground">
              Media and Meditation 🧘
            </h1>
            <p className="text-sm sm:text-base text-muted-foreground mt-1">
              Find your inner peace with guided meditations and custom sessions
            </p>
          </div>
          {youtubeConnection?.connected ? (
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800">
                <Youtube className="w-5 h-5 text-red-500" />
                <span className="text-sm font-medium text-green-700 dark:text-green-300">
                  {youtubeConnection.channelName || youtubeConnection.email || "Connected"}
                </span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => disconnectYoutubeMutation.mutate()}
                disabled={disconnectYoutubeMutation.isPending}
                data-testid="button-disconnect-youtube"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          ) : (
            <Button
              variant="outline"
              className="flex items-center gap-2 border-red-500 text-red-500 hover:bg-red-50 dark:hover:bg-red-950"
              onClick={() => window.location.href = '/api/youtube/connect'}
              data-testid="button-connect-youtube"
            >
              <Youtube className="w-5 h-5" />
              Connect YouTube Account
            </Button>
          )}
        </div>
      </div>

      <div className="px-4 sm:px-6 lg:px-8 py-8">
        <div className="w-full max-w-full flex flex-col gap-6">
          {playMode === "none" ? (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleSectionDragEnd}
            >
              <SortableContext
                items={sectionOrder}
                strategy={verticalListSortingStrategy}
              >
                {/* Quick Actions Card */}
                <SortableSection id="quick-actions" isMobile={isMobile} order={sectionOrder.indexOf('quick-actions')}>
                  <Card className="hover:shadow-lg transition-shadow" data-testid="card-quick-actions">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Play className="w-6 h-6 text-primary" />
                    Quick Play
                  </CardTitle>
                  <CardDescription>
                    Start your meditation session
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Button 
                      className="bg-primary hover:bg-primary/90 text-primary-foreground"
                      onClick={handlePlayPlaylist}
                      disabled={songs.length === 0}
                      data-testid="button-play-playlist"
                    >
                      <Play className="w-4 h-4 mr-2" />
                      Play All Tracks ({songs.length})
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setShowPlaylistDialog(true)}
                      data-testid="button-customize-playlists"
                    >
                      <ListMusic className="w-4 h-4 mr-2" />
                      Manage Playlists
                    </Button>
                  </div>
                  <Button
                    variant="ghost"
                    className="w-full mt-3"
                    onClick={() => setLocation("/meditation/settings")}
                    data-testid="button-meditation-settings"
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    Settings
                  </Button>
                  </CardContent>
                  </Card>
                </SortableSection>

                {/* Playlists Section */}
                <SortableSection id="playlists" isMobile={isMobile} order={sectionOrder.indexOf('playlists')}>
                    <Card data-testid="card-playlists-section">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="flex items-center gap-2">
                          <ListMusic className="w-6 h-6 text-primary" />
                          My Playlists
                        </CardTitle>
                        <CardDescription>
                          {isMobile ? "Tap to play" : "Drag to reorder, click to play"}
                        </CardDescription>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowPlaylistDialog(true)}
                        data-testid="button-add-playlist"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        New
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handlePlaylistDragEnd}
                    >
                      <SortableContext
                        items={playlists.map(p => p.id)}
                        strategy={verticalListSortingStrategy}
                      >
                        <div className="space-y-2">
                          {playlists.length === 0 && (
                            <p className="text-sm text-muted-foreground text-center py-4" data-testid="text-no-playlists">
                              No playlists yet. Click "New" to create your first playlist.
                            </p>
                          )}
                          {playlists.map((playlist) => (
                            <SortablePlaylistRow 
                              key={playlist.id} 
                              playlist={playlist} 
                              onPlay={handlePlaySpecificPlaylist}
                              onEdit={() => {
                                setEditingPlaylist(playlist);
                                setEditPlaylistName(playlist.name);
                              }}
                              onDelete={() => {
                                if (confirm(`Are you sure you want to delete "${playlist.name}"? This will also delete all media in this playlist.`)) {
                                  deletePlaylistMutation.mutate(playlist.id);
                                }
                              }}
                              onShare={() => setSharingPlaylist(playlist)}
                              isMobile={isMobile}
                            />
                          ))}
                        </div>
                      </SortableContext>
                    </DndContext>
                    </CardContent>
                    </Card>

                    {/* Edit Playlist Dialog */}
                    <Dialog open={!!editingPlaylist} onOpenChange={(open) => !open && setEditingPlaylist(null)}>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Edit Playlist</DialogTitle>
                    <DialogDescription>Change the name of your playlist</DialogDescription>
                  </DialogHeader>
                  <div className="py-4">
                    <Input
                      value={editPlaylistName}
                      onChange={(e) => setEditPlaylistName(e.target.value)}
                      placeholder="Playlist name"
                      data-testid="input-edit-playlist-name"
                    />
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setEditingPlaylist(null)}>
                      Cancel
                    </Button>
                    <Button
                      onClick={() => {
                        if (editingPlaylist && editPlaylistName.trim()) {
                          updatePlaylistMutation.mutate({ id: editingPlaylist.id, name: editPlaylistName.trim() });
                        }
                      }}
                      disabled={!editPlaylistName.trim() || updatePlaylistMutation.isPending}
                      data-testid="button-save-playlist"
                    >
                      {updatePlaylistMutation.isPending ? "Saving..." : "Save"}
                    </Button>
                  </DialogFooter>
                    </DialogContent>
                  </Dialog>

                  {/* Share Playlist Dialog */}
                  <Dialog open={!!sharingPlaylist} onOpenChange={(open) => !open && setSharingPlaylist(null)}>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                          <Share2 className="w-5 h-5" />
                          Share Playlist
                        </DialogTitle>
                        <DialogDescription>
                          Share "{sharingPlaylist?.name}" with a friend
                        </DialogDescription>
                      </DialogHeader>
                      <div className="py-4">
                        {friends.length === 0 ? (
                          <p className="text-muted-foreground text-center py-4">
                            You don't have any friends yet. Add friends in the Community section to share playlists.
                          </p>
                        ) : (
                          <Select value={selectedFriendId} onValueChange={setSelectedFriendId}>
                            <SelectTrigger data-testid="select-friend-to-share">
                              <SelectValue placeholder="Select a friend" />
                            </SelectTrigger>
                            <SelectContent>
                              {friends.map((friendship) => (
                                <SelectItem key={friendship.friend.id} value={friendship.friend.id}>
                                  {friendship.friend.firstName && friendship.friend.lastName 
                                    ? `${friendship.friend.firstName} ${friendship.friend.lastName}`
                                    : friendship.friend.username || friendship.friend.email || "Unknown"}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                      <DialogFooter>
                        <Button variant="outline" onClick={() => {
                          setSharingPlaylist(null);
                          setSelectedFriendId("");
                        }}>
                          Cancel
                        </Button>
                        <Button
                          onClick={() => {
                            if (sharingPlaylist && selectedFriendId) {
                              sharePlaylistMutation.mutate({ 
                                playlistId: sharingPlaylist.id, 
                                friendId: selectedFriendId 
                              });
                            }
                          }}
                          disabled={!selectedFriendId || sharePlaylistMutation.isPending}
                          data-testid="button-share-playlist"
                        >
                          {sharePlaylistMutation.isPending ? "Sharing..." : "Share"}
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                  </SortableSection>

                {/* Shared Playlists Section */}
                {sharedPlaylists.length > 0 && (
                  <Card data-testid="card-shared-playlists" className="mb-4">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Users className="w-6 h-6 text-primary" />
                        Shared with Me
                      </CardTitle>
                      <CardDescription>
                        Playlists your friends have shared with you
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {sharedPlaylists.map((shared) => (
                          <div 
                            key={shared.id}
                            className="flex items-center gap-3 p-3 border rounded-lg hover:bg-accent/50 transition-colors cursor-pointer"
                            onClick={() => handlePlaySpecificPlaylist(shared.playlist, shared.songs)}
                            data-testid={`shared-playlist-${shared.id}`}
                          >
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <ListMusic className="w-5 h-5 text-primary flex-shrink-0" />
                                <h4 className="font-medium text-sm truncate">{shared.playlist.name}</h4>
                                <span className="text-xs text-muted-foreground">
                                  ({shared.songs.length} {shared.songs.length === 1 ? 'track' : 'tracks'})
                                </span>
                              </div>
                              <p className="text-xs text-muted-foreground mt-1">
                                Shared by {shared.owner.firstName && shared.owner.lastName 
                                  ? `${shared.owner.firstName} ${shared.owner.lastName}`
                                  : shared.owner.username || shared.owner.email || "Unknown"}
                              </p>
                            </div>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8"
                              onClick={(e) => {
                                e.stopPropagation();
                                handlePlaySpecificPlaylist(shared.playlist, shared.songs);
                              }}
                            >
                              <Play className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* My Custom Meditations Section */}
                {customMeditations.length > 0 && (
                  <SortableSection id="custom-meditations" isMobile={isMobile} order={sectionOrder.indexOf('custom-meditations')}>
                    <Card data-testid="card-custom-meditations">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Mic className="w-6 h-6 text-green-500" />
                          My Custom Meditations
                        </CardTitle>
                        <CardDescription>
                          Your personalized meditation recordings
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          {customMeditations.map((meditation) => (
                            <div
                              key={meditation.id}
                              className="flex items-center justify-between p-3 rounded-lg border hover:bg-accent/50 cursor-pointer transition-colors"
                              onClick={() => handlePlayCustomMeditation(meditation.id)}
                              data-testid={`custom-meditation-item-${meditation.id}`}
                            >
                              <div className="flex items-center gap-3">
                                <Mic className="w-5 h-5 text-green-500" />
                                <div>
                                  <p className="font-medium text-sm">{meditation.name}</p>
                                  <p className="text-xs text-muted-foreground">
                                    {meditation.voiceType === 'recorded' ? 'Recorded' : meditation.voiceType} voice
                                  </p>
                                </div>
                              </div>
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-8 w-8"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handlePlayCustomMeditation(meditation.id);
                                }}
                                data-testid={`button-play-custom-${meditation.id}`}
                              >
                                <Play className="w-4 h-4" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  </SortableSection>
                )}

                {/* Individual Track List - My Tracks */}
                {myTracks.length > 0 && (
                  <SortableSection id="tracks" isMobile={isMobile} order={sectionOrder.indexOf('tracks')}>
                    <Card data-testid="card-song-list">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Volume2 className="w-6 h-6 text-primary" />
                          My Tracks
                        </CardTitle>
                        <CardDescription>
                          {isMobile ? "Click on any track to play it" : "Drag to reorder, click to play"}
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <DndContext
                          sensors={sensors}
                          collisionDetection={closestCenter}
                          onDragEnd={handleTracksDragEnd}
                        >
                          <SortableContext
                            items={myTracks.map(t => t.id)}
                            strategy={verticalListSortingStrategy}
                          >
                            <div className="space-y-2">
                              {myTracks.map((track) => (
                                <SortableTrackRow
                                  key={track.id}
                                  track={track}
                                  onPlaySong={handlePlaySong}
                                  onPlayCustom={handlePlayCustomMeditation}
                                  isMobile={isMobile}
                                />
                              ))}
                            </div>
                          </SortableContext>
                        </DndContext>
                      </CardContent>
                    </Card>
                  </SortableSection>
                )}

                {/* Meditation Time Tracking Stats */}
                <SortableSection id="stats" isMobile={isMobile} order={sectionOrder.indexOf('stats')}>
                  <Card data-testid="card-meditation-stats">
                <CardHeader>
                  <div className="flex flex-col gap-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="flex items-center gap-2">
                        <Clock className="w-6 h-6 text-primary" />
                        Meditation Time
                      </CardTitle>
                      <div className="flex gap-1 flex-wrap">
                        <Button
                          variant={statsFilter === "day" ? "default" : "outline"}
                          size="sm"
                          onClick={() => setStatsFilter("day")}
                          data-testid="button-stats-day"
                        >
                          Today
                        </Button>
                        <Button
                          variant={statsFilter === "week" ? "default" : "outline"}
                          size="sm"
                          onClick={() => setStatsFilter("week")}
                          data-testid="button-stats-week"
                        >
                          Week
                        </Button>
                        <Button
                          variant={statsFilter === "month" ? "default" : "outline"}
                          size="sm"
                          onClick={() => setStatsFilter("month")}
                          data-testid="button-stats-month"
                        >
                          Month
                        </Button>
                        <Button
                          variant={statsFilter === "custom" ? "default" : "outline"}
                          size="sm"
                          onClick={() => setStatsFilter("custom")}
                          data-testid="button-stats-custom"
                        >
                          Custom
                        </Button>
                      </div>
                    </div>
                    {statsFilter === "custom" && (
                      <div className="flex flex-wrap items-center gap-2 pt-2 border-t">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-muted-foreground">From:</span>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                className="w-[130px] justify-start text-left font-normal"
                                data-testid="button-start-date"
                              >
                                <CalendarIcon className="mr-2 h-4 w-4" />
                                {customStartDate ? format(customStartDate, "MMM d, yyyy") : "Start date"}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                              <Calendar
                                mode="single"
                                selected={customStartDate}
                                onSelect={setCustomStartDate}
                                disabled={(date) => date > new Date() || (customEndDate ? date > customEndDate : false)}
                                initialFocus
                                data-testid="calendar-start-date"
                              />
                            </PopoverContent>
                          </Popover>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-muted-foreground">To:</span>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                className="w-[130px] justify-start text-left font-normal"
                                data-testid="button-end-date"
                              >
                                <CalendarIcon className="mr-2 h-4 w-4" />
                                {customEndDate ? format(customEndDate, "MMM d, yyyy") : "End date"}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                              <Calendar
                                mode="single"
                                selected={customEndDate}
                                onSelect={setCustomEndDate}
                                disabled={(date) => date > new Date() || (customStartDate ? date < customStartDate : false)}
                                initialFocus
                                data-testid="calendar-end-date"
                              />
                            </PopoverContent>
                          </Popover>
                        </div>
                        {(customStartDate || customEndDate) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setCustomStartDate(undefined);
                              setCustomEndDate(undefined);
                            }}
                            data-testid="button-clear-dates"
                          >
                            <X className="h-4 w-4 mr-1" />
                            Clear
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                  <CardDescription className="mt-2">
                    Track your meditation practice
                    {statsFilter === "custom" && customStartDate && customEndDate && (
                      <span className="ml-1">
                        ({format(customStartDate, "MMM d")} - {format(customEndDate, "MMM d, yyyy")})
                      </span>
                    )}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="text-center p-4 rounded-lg bg-primary/10">
                      <div className="text-3xl font-bold text-primary" data-testid="text-total-time">
                        {meditationStats ? formatDuration(meditationStats.totalSeconds) : "0 min"}
                      </div>
                      <div className="text-sm text-muted-foreground">Total Time</div>
                    </div>
                    <div className="text-center p-4 rounded-lg bg-primary/10">
                      <div className="text-3xl font-bold text-primary" data-testid="text-session-count">
                        {meditationStats?.sessionCount || 0}
                      </div>
                      <div className="text-sm text-muted-foreground">Sessions</div>
                    </div>
                  </div>
                  {meditationStats && meditationStats.sessionCount > 0 && (
                    <div className="mt-4 text-center text-sm text-muted-foreground">
                      Average: {formatDuration(Math.floor(meditationStats.totalSeconds / meditationStats.sessionCount))} per session
                    </div>
                  )}
                  </CardContent>
                  </Card>
                </SortableSection>

                {/* Meditation Goal Tracking */}
                <SortableSection id="goal" isMobile={isMobile} order={sectionOrder.indexOf('goal')}>
                  <Card data-testid="card-meditation-goal">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"/>
                      <circle cx="12" cy="12" r="6"/>
                      <circle cx="12" cy="12" r="2"/>
                    </svg>
                    Meditation Goal
                  </CardTitle>
                  <CardDescription>
                    Set a time goal for your meditation practice
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Minute Goal Input */}
                  <div className="space-y-2">
                    <Label htmlFor="minute-goal">Minute Goal</Label>
                    <div className="flex gap-2">
                      <NumberField
                        id="minute-goal"
                        min={1}
                        placeholder="Enter minutes..."
                        value={goalMinutes}
                        onValueChange={(value) => {
                          setGoalMinutes(value);
                          saveGoalSettingsMutation.mutate({
                            meditationGoalMinutes: value,
                          });
                        }}
                        className="w-32"
                        data-testid="input-goal-minutes"
                      />
                      <span className="text-sm text-muted-foreground self-center">minutes</span>
                    </div>
                  </div>

                  {/* Goal Time Period */}
                  <div className="space-y-2">
                    <Label>Goal Time Period</Label>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        variant={goalPeriod === "week" ? "default" : "outline"}
                        size="sm"
                        onClick={() => {
                          setGoalPeriod("week");
                          saveGoalSettingsMutation.mutate({
                            meditationGoalPeriod: "week",
                            meditationGoalStartDate: null,
                            meditationGoalEndDate: null,
                          });
                        }}
                        data-testid="button-goal-week"
                      >
                        This Week
                      </Button>
                      <Button
                        variant={goalPeriod === "month" ? "default" : "outline"}
                        size="sm"
                        onClick={() => {
                          setGoalPeriod("month");
                          saveGoalSettingsMutation.mutate({
                            meditationGoalPeriod: "month",
                            meditationGoalStartDate: null,
                            meditationGoalEndDate: null,
                          });
                        }}
                        data-testid="button-goal-month"
                      >
                        This Month
                      </Button>
                      <Button
                        variant={goalPeriod === "custom" ? "default" : "outline"}
                        size="sm"
                        onClick={() => {
                          setGoalPeriod("custom");
                          saveGoalSettingsMutation.mutate({
                            meditationGoalPeriod: "custom",
                          });
                        }}
                        data-testid="button-goal-custom"
                      >
                        Custom
                      </Button>
                    </div>

                    {/* Custom Date Range for Goal */}
                    {goalPeriod === "custom" && (
                      <div className="flex flex-wrap items-center gap-2 pt-2">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-muted-foreground">From:</span>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                className="w-[130px] justify-start text-left font-normal"
                                data-testid="button-goal-start-date"
                              >
                                <CalendarIcon className="mr-2 h-4 w-4" />
                                {goalStartDate ? format(goalStartDate, "MMM d, yyyy") : "Start date"}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                              <Calendar
                                mode="single"
                                selected={goalStartDate}
                                onSelect={(date) => {
                                  setGoalStartDate(date);
                                  saveGoalSettingsMutation.mutate({
                                    meditationGoalStartDate: date?.toISOString() || null,
                                  });
                                }}
                                disabled={(date) => date > new Date() || (goalEndDate ? date > goalEndDate : false)}
                                initialFocus
                                data-testid="calendar-goal-start-date"
                              />
                            </PopoverContent>
                          </Popover>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-muted-foreground">To:</span>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                className="w-[130px] justify-start text-left font-normal"
                                data-testid="button-goal-end-date"
                              >
                                <CalendarIcon className="mr-2 h-4 w-4" />
                                {goalEndDate ? format(goalEndDate, "MMM d, yyyy") : "End date"}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                              <Calendar
                                mode="single"
                                selected={goalEndDate}
                                onSelect={(date) => {
                                  setGoalEndDate(date);
                                  saveGoalSettingsMutation.mutate({
                                    meditationGoalEndDate: date?.toISOString() || null,
                                  });
                                }}
                                disabled={(date) => date > new Date() || (goalStartDate ? date < goalStartDate : false)}
                                initialFocus
                                data-testid="calendar-goal-end-date"
                              />
                            </PopoverContent>
                          </Popover>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Goal Score Display */}
                  {goalMinutes && (
                    <div className="pt-4 border-t space-y-3">
                      <Label>Goal Score</Label>
                      <div className="grid grid-cols-3 gap-3">
                        <div className="text-center p-3 rounded-lg bg-primary/10">
                          <div className="text-2xl font-bold text-primary" data-testid="text-goal-target">
                            {goalMinutes}
                          </div>
                          <div className="text-xs text-muted-foreground">Goal (min)</div>
                        </div>
                        <div className="text-center p-3 rounded-lg bg-primary/10">
                          <div className="text-2xl font-bold text-primary" data-testid="text-goal-completed">
                            {goalMinutesCompleted}
                          </div>
                          <div className="text-xs text-muted-foreground">Completed (min)</div>
                        </div>
                        <div className={`text-center p-3 rounded-lg ${goalScore >= 0 ? 'bg-green-100 dark:bg-green-900/30' : 'bg-red-100 dark:bg-red-900/30'}`}>
                          <div className={`text-2xl font-bold ${goalScore >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`} data-testid="text-goal-score">
                            {goalScore >= 0 ? '+' : ''}{goalScore}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {goalScore >= 0 ? 'Ahead' : 'Behind'} (min)
                          </div>
                        </div>
                      </div>
                      <div className="w-full bg-secondary rounded-full h-3">
                        <div
                          className={`h-3 rounded-full transition-all ${goalMinutesCompleted >= goalMinutes ? 'bg-green-500' : 'bg-primary'}`}
                          style={{ width: `${Math.min(100, (goalMinutesCompleted / goalMinutes) * 100)}%` }}
                          data-testid="progress-goal"
                        />
                      </div>
                      <div className="text-center text-sm text-muted-foreground">
                        {Math.round((goalMinutesCompleted / goalMinutes) * 100)}% of goal completed
                        {goalPeriod === "week" && " this week"}
                        {goalPeriod === "month" && " this month"}
                        {goalPeriod === "custom" && goalStartDate && goalEndDate && (
                          <span> ({format(goalStartDate, "MMM d")} - {format(goalEndDate, "MMM d")})</span>
                        )}
                      </div>
                    </div>
                  )}
                  </CardContent>
                  </Card>
                </SortableSection>
              </SortableContext>
            </DndContext>
          ) : playMode === "playlist" ? (
            <Card data-testid="audio-player-card">
              <CardHeader>
                <CardTitle>Now Playing - Playlist</CardTitle>
                <CardDescription>
                  {playerState.currentSong?.title || "No media loaded"}
                  {playerState.currentSong?.artist && ` • ${playerState.currentSong.artist}`}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm text-muted-foreground">
                    <span>{formatTime(playerState.currentTime)}</span>
                    <span>{formatTime(playerState.duration)}</span>
                  </div>
                  <div 
                    className="w-full bg-secondary rounded-full h-2 cursor-pointer"
                    onClick={(e) => {
                      const rect = e.currentTarget.getBoundingClientRect();
                      const x = e.clientX - rect.left;
                      const percentage = x / rect.width;
                      playerControls.seek(percentage * playerState.duration);
                    }}
                  >
                    <div
                      className="bg-primary h-2 rounded-full transition-all"
                      style={{ width: `${(playerState.currentTime / playerState.duration) * 100}%` }}
                    />
                  </div>
                </div>

                <div className="flex items-center justify-center gap-4">
                  <Button
                    size="icon"
                    variant="outline"
                    onClick={playerControls.previous}
                    disabled={playerState.currentIndex === 0}
                    data-testid="button-previous"
                  >
                    <SkipBack className="w-5 h-5" />
                  </Button>

                  {playerState.isPlaying ? (
                    <Button
                      size="icon"
                      className="h-12 w-12"
                      onClick={playerControls.pause}
                      data-testid="button-pause"
                    >
                      <Pause className="w-6 h-6" />
                    </Button>
                  ) : (
                    <Button
                      size="icon"
                      className="h-12 w-12"
                      onClick={playerControls.play}
                      data-testid="button-play"
                    >
                      <Play className="w-6 h-6" />
                    </Button>
                  )}

                  <Button
                    size="icon"
                    variant="outline"
                    onClick={playerControls.next}
                    disabled={playerState.currentIndex === playerState.playlist.length - 1}
                    data-testid="button-next"
                  >
                    <SkipForward className="w-5 h-5" />
                  </Button>

                  <Button
                    size="icon"
                    variant="outline"
                    onClick={handleStop}
                    data-testid="button-stop"
                  >
                    <Square className="w-5 h-5" />
                  </Button>
                </div>

                <div className="flex items-center gap-3">
                  <Volume2 className="w-5 h-5 text-muted-foreground" />
                  <Slider
                    value={[playerState.volume]}
                    onValueChange={(vals) => playerControls.setVolume(vals[0])}
                    max={100}
                    step={1}
                    className="flex-1"
                    data-testid="slider-volume"
                  />
                  <span className="text-sm text-muted-foreground w-12 text-right">
                    {playerState.volume}%
                  </span>
                </div>

                <div className="text-center text-sm text-muted-foreground">
                  Track {playerState.currentIndex + 1} of {playerState.playlist.length}
                </div>
              </CardContent>
            </Card>
          ) : playMode === "single" && currentSingleSong ? (
            <Card data-testid="single-song-player-card">
              <CardHeader>
                <CardTitle>Now Playing</CardTitle>
                <CardDescription>
                  {currentSingleSong.title}
                  {currentSingleSong.artist && ` • ${currentSingleSong.artist}`}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm text-muted-foreground">
                    <span>{formatTime(playerState.currentTime)}</span>
                    <span>{formatTime(playerState.duration)}</span>
                  </div>
                  <div 
                    className="w-full bg-secondary rounded-full h-2 cursor-pointer"
                    onClick={(e) => {
                      const rect = e.currentTarget.getBoundingClientRect();
                      const x = e.clientX - rect.left;
                      const percentage = x / rect.width;
                      playerControls.seek(percentage * playerState.duration);
                    }}
                  >
                    <div
                      className="bg-primary h-2 rounded-full transition-all"
                      style={{ width: `${(playerState.currentTime / playerState.duration) * 100}%` }}
                    />
                  </div>
                </div>

                <div className="flex items-center justify-center gap-4">
                  {playerState.isPlaying ? (
                    <Button
                      size="icon"
                      className="h-12 w-12"
                      onClick={playerControls.pause}
                      data-testid="button-pause-single"
                    >
                      <Pause className="w-6 h-6" />
                    </Button>
                  ) : (
                    <Button
                      size="icon"
                      className="h-12 w-12"
                      onClick={playerControls.play}
                      data-testid="button-play-single"
                    >
                      <Play className="w-6 h-6" />
                    </Button>
                  )}

                  <Button
                    size="icon"
                    variant="outline"
                    onClick={handleStop}
                    data-testid="button-stop-single"
                  >
                    <Square className="w-5 h-5" />
                  </Button>
                </div>

                <div className="flex items-center gap-3">
                  <Volume2 className="w-5 h-5 text-muted-foreground" />
                  <Slider
                    value={[playerState.volume]}
                    onValueChange={(vals) => playerControls.setVolume(vals[0])}
                    max={100}
                    step={1}
                    className="flex-1"
                    data-testid="slider-volume-single"
                  />
                  <span className="text-sm text-muted-foreground w-12 text-right">
                    {playerState.volume}%
                  </span>
                </div>
              </CardContent>
            </Card>
          ) : playMode === "custom" && currentMeditation ? (
            <Card data-testid="custom-meditation-player">
              <CardHeader>
                <CardTitle>Now Playing - {currentMeditation.name}</CardTitle>
                <CardDescription>
                  {currentMeditation.voiceType} voice • Background music at {currentMeditation.musicVolume}%
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {currentMeditation.voiceType === "recorded" && (
                  <>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm text-muted-foreground">
                        <span>{formatTime(mixerState.currentTime)}</span>
                        <span>{formatTime(mixerState.duration)}</span>
                      </div>
                      <div className="w-full bg-secondary rounded-full h-2">
                        <div
                          className="bg-primary h-2 rounded-full transition-all"
                          style={{ width: `${(mixerState.currentTime / mixerState.duration) * 100}%` }}
                        />
                      </div>
                    </div>

                    <div className="flex items-center justify-center gap-4">
                      {mixerState.isPlaying ? (
                        <Button
                          size="icon"
                          className="h-12 w-12"
                          onClick={mixerControls.pause}
                          data-testid="button-pause-mixer"
                        >
                          <Pause className="w-6 h-6" />
                        </Button>
                      ) : (
                        <Button
                          size="icon"
                          className="h-12 w-12"
                          onClick={mixerControls.play}
                          data-testid="button-play-mixer"
                        >
                          <Play className="w-6 h-6" />
                        </Button>
                      )}

                      <Button
                        size="icon"
                        variant="outline"
                        onClick={handleStop}
                        data-testid="button-stop-custom"
                      >
                        <Square className="w-5 h-5" />
                      </Button>
                    </div>

                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <Volume2 className="w-5 h-5 text-muted-foreground" />
                        <div className="flex-1">
                          <label className="text-xs text-muted-foreground">Music</label>
                          <Slider
                            value={[mixerState.musicVolume]}
                            onValueChange={(vals) => mixerControls.setMusicVolume(vals[0])}
                            max={100}
                            step={1}
                            data-testid="slider-music-volume-player"
                          />
                        </div>
                        <span className="text-sm text-muted-foreground w-12 text-right">
                          {mixerState.musicVolume}%
                        </span>
                      </div>

                      <div className="flex items-center gap-3">
                        <Volume2 className="w-5 h-5 text-muted-foreground" />
                        <div className="flex-1">
                          <label className="text-xs text-muted-foreground">Voice</label>
                          <Slider
                            value={[mixerState.voiceVolume]}
                            onValueChange={(vals) => mixerControls.setVoiceVolume(vals[0])}
                            max={100}
                            step={1}
                            data-testid="slider-voice-volume-player"
                          />
                        </div>
                        <span className="text-sm text-muted-foreground w-12 text-right">
                          {mixerState.voiceVolume}%
                        </span>
                      </div>
                    </div>
                  </>
                )}

                {(currentMeditation.voiceType === "male" || currentMeditation.voiceType === "female") && (
                  <div className="text-center">
                    <div className="flex items-center justify-center gap-4 mb-4">
                      {ttsState.isSpeaking ? (
                        <Button
                          size="icon"
                          className="h-12 w-12"
                          onClick={ttsControls.pause}
                          data-testid="button-pause-tts"
                        >
                          <Pause className="w-6 h-6" />
                        </Button>
                      ) : (
                        <Button
                          size="icon"
                          className="h-12 w-12"
                          onClick={() => ttsControls.speak(currentMeditation.scriptText)}
                          data-testid="button-play-tts"
                        >
                          <Play className="w-6 h-6" />
                        </Button>
                      )}

                      <Button
                        size="icon"
                        variant="outline"
                        onClick={handleStop}
                        data-testid="button-stop-tts"
                      >
                        <Square className="w-5 h-5" />
                      </Button>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {ttsState.isSpeaking ? "Speaking..." : "Click play to start meditation"}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          ) : playMode === "youtube" && currentYouTubeVideoId ? (
            <Card data-testid="youtube-player-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Youtube className="w-6 h-6 text-red-500" />
                  Now Playing - YouTube
                </CardTitle>
                <CardDescription>
                  {currentYouTubeSong?.title || "YouTube Video"}
                  {currentYouTubeSong?.artist && ` • ${currentYouTubeSong.artist}`}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* YouTube Embed Error State - show when video can't be embedded */}
                {youtubeEmbedError ? (
                  <div className="rounded-lg bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 p-6 text-center space-y-4" data-testid="youtube-embed-error">
                    <div className="flex justify-center">
                      <AlertCircle className="w-12 h-12 text-red-500" />
                    </div>
                    <h3 className="text-lg font-semibold text-red-800 dark:text-red-200">Video Cannot Be Embedded</h3>
                    <p className="text-sm text-red-600 dark:text-red-300">
                      The video owner has disabled embedding for this video, or the video is unavailable. 
                      You can still watch it directly on YouTube.
                    </p>
                    <Button
                      onClick={() => window.open(`https://www.youtube.com/watch?v=${currentYouTubeVideoId}`, '_blank')}
                      className="bg-red-600 hover:bg-red-700 text-white"
                      data-testid="button-open-youtube-error"
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Watch on YouTube
                    </Button>
                  </div>
                ) : (
                  <>
                    {/* YouTube Embedded Player - key forces complete iframe recreation on video change */}
                    <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                      <iframe
                        ref={youtubeIframeRef}
                        key={`youtube-${currentYouTubeVideoId}`}
                        className="absolute top-0 left-0 w-full h-full rounded-lg"
                        src={`https://www.youtube-nocookie.com/embed/${currentYouTubeVideoId}?autoplay=1&rel=0&enablejsapi=1&origin=${window.location.origin}`}
                        title={currentYouTubeSong?.title || "YouTube Video"}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        referrerPolicy="strict-origin-when-cross-origin"
                        data-testid="youtube-iframe"
                      />
                    </div>
                  </>
                )}

                <div className="flex items-center justify-center gap-4 pt-4">
                  <Button
                    size="icon"
                    variant="outline"
                    onClick={handleStop}
                    data-testid="button-stop-youtube"
                  >
                    <Square className="w-5 h-5" />
                  </Button>
                  {!youtubeEmbedError && (
                    <Button
                      variant="outline"
                      onClick={() => window.open(`https://www.youtube.com/watch?v=${currentYouTubeVideoId}`, '_blank')}
                      data-testid="button-open-youtube"
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Open in YouTube
                    </Button>
                  )}
                </div>

                {!youtubeEmbedError && (
                  <p className="text-center text-sm text-muted-foreground">
                    Video player controls are handled by YouTube. If video doesn't play, try "Open in YouTube".
                  </p>
                )}
              </CardContent>
            </Card>
          ) : null}

          {/* MnM Library - Combined Media & Custom Meditations */}
          <div className="mt-8">
            <Card data-testid="meditation-library-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Music className="w-5 h-5" />
                  MnM Library
                </CardTitle>
                <CardDescription>
                  Browse all your media and custom meditations in one place
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Search and Filters */}
                <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                  <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      type="text"
                      placeholder="Search by title, artist, or subject..."
                      value={librarySearchQuery}
                      onChange={(e) => setLibrarySearchQuery(e.target.value)}
                      className="pl-10"
                      data-testid="input-library-search"
                    />
                  </div>
                  
                  <div className="flex items-center gap-4 flex-wrap">
                    {/* Starred filter */}
                    <div className="flex items-center gap-2">
                      <Switch
                        id="starred-only"
                        checked={libraryStarredOnly}
                        onCheckedChange={setLibraryStarredOnly}
                        data-testid="switch-starred-only"
                      />
                      <Label htmlFor="starred-only" className="flex items-center gap-1 cursor-pointer">
                        <Star className={`w-4 h-4 ${libraryStarredOnly ? 'fill-yellow-400 text-yellow-400' : ''}`} />
                        Starred Only
                      </Label>
                    </div>
                    
                    {/* Tag filter dropdown */}
                    {allTags.length > 0 && (
                      <Select
                        value={librarySelectedTags[0] || "all"}
                        onValueChange={(value) => {
                          if (value === "all") {
                            setLibrarySelectedTags([]);
                          } else {
                            setLibrarySelectedTags([value]);
                          }
                        }}
                      >
                        <SelectTrigger className="w-40" data-testid="select-tag-filter">
                          <Tag className="w-4 h-4 mr-2" />
                          <SelectValue placeholder="Filter by tag" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Tags</SelectItem>
                          {allTags.map((tag) => (
                            <SelectItem key={tag} value={tag}>
                              {tag}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}

                    {/* MnM Category filter with add/remove */}
                    <Popover open={categoryPopoverOpen} onOpenChange={setCategoryPopoverOpen}>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="min-w-[140px] justify-start" data-testid="button-category-filter">
                          <Filter className="w-4 h-4 mr-2" />
                          {librarySelectedCategoryId 
                            ? mnmCategories.find(c => c.id === librarySelectedCategoryId)?.name || "Category"
                            : "All Categories"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-64 p-0" align="start">
                        <div className="p-2 border-b">
                          <p className="text-sm font-medium mb-2">MnM Categories</p>
                          <div className="flex gap-2">
                            <Input
                              placeholder="New category..."
                              value={newCategoryName}
                              onChange={(e) => setNewCategoryName(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && newCategoryName.trim()) {
                                  createCategoryMutation.mutate(newCategoryName.trim());
                                }
                              }}
                              className="h-8 text-sm"
                              data-testid="input-new-category"
                            />
                            <Button
                              size="sm"
                              className="h-8 px-2"
                              onClick={() => {
                                if (newCategoryName.trim()) {
                                  createCategoryMutation.mutate(newCategoryName.trim());
                                }
                              }}
                              disabled={!newCategoryName.trim() || createCategoryMutation.isPending}
                              data-testid="button-add-category"
                            >
                              <Plus className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                        <div className="max-h-48 overflow-y-auto">
                          <button
                            className={`w-full px-3 py-2 text-left text-sm hover:bg-accent ${!librarySelectedCategoryId ? 'bg-accent' : ''}`}
                            onClick={() => {
                              setLibrarySelectedCategoryId(null);
                              setCategoryPopoverOpen(false);
                            }}
                            data-testid="button-category-all"
                          >
                            All Categories
                          </button>
                          {mnmCategories.map((category) => (
                            <div
                              key={category.id}
                              className={`flex items-center justify-between px-3 py-2 hover:bg-accent ${librarySelectedCategoryId === category.id ? 'bg-accent' : ''}`}
                            >
                              <button
                                className="flex-1 text-left text-sm flex items-center gap-2"
                                onClick={() => {
                                  setLibrarySelectedCategoryId(category.id);
                                  setCategoryPopoverOpen(false);
                                }}
                                data-testid={`button-category-${category.id}`}
                              >
                                <div 
                                  className="w-3 h-3 rounded-full" 
                                  style={{ backgroundColor: category.color }}
                                />
                                {category.name}
                              </button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0 opacity-50 hover:opacity-100"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (confirm(`Delete category "${category.name}"?`)) {
                                    deleteCategoryMutation.mutate(category.id);
                                  }
                                }}
                                data-testid={`button-delete-category-${category.id}`}
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          ))}
                          {mnmCategories.length === 0 && (
                            <div className="px-3 py-2 text-sm text-muted-foreground">
                              No categories yet
                            </div>
                          )}
                        </div>
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>

                {/* Clear filters */}
                {(librarySearchQuery || libraryStarredOnly || librarySelectedTags.length > 0 || librarySelectedCategoryId) && (
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm text-muted-foreground">Active filters:</span>
                    {librarySearchQuery && (
                      <Badge variant="secondary" className="flex items-center gap-1">
                        Search: {librarySearchQuery}
                        <X
                          className="w-3 h-3 cursor-pointer"
                          onClick={() => setLibrarySearchQuery("")}
                        />
                      </Badge>
                    )}
                    {libraryStarredOnly && (
                      <Badge variant="secondary" className="flex items-center gap-1">
                        Starred
                        <X
                          className="w-3 h-3 cursor-pointer"
                          onClick={() => setLibraryStarredOnly(false)}
                        />
                      </Badge>
                    )}
                    {librarySelectedTags.map((tag) => (
                      <Badge key={tag} variant="secondary" className="flex items-center gap-1">
                        {tag}
                        <X
                          className="w-3 h-3 cursor-pointer"
                          onClick={() => setLibrarySelectedTags(librarySelectedTags.filter((t) => t !== tag))}
                        />
                      </Badge>
                    ))}
                    {librarySelectedCategoryId && (
                      <Badge variant="secondary" className="flex items-center gap-1">
                        {mnmCategories.find(c => c.id === librarySelectedCategoryId)?.name || "Category"}
                        <X
                          className="w-3 h-3 cursor-pointer"
                          onClick={() => setLibrarySelectedCategoryId(null)}
                        />
                      </Badge>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setLibrarySearchQuery("");
                        setLibraryStarredOnly(false);
                        setLibrarySelectedTags([]);
                        setLibrarySelectedCategoryId(null);
                      }}
                      data-testid="button-clear-filters"
                    >
                      Clear all
                    </Button>
                  </div>
                )}

                {/* Library items */}
                {libraryLoading ? (
                  <div className="text-center py-8 text-muted-foreground">Loading library...</div>
                ) : libraryItems.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    {librarySearchQuery || libraryStarredOnly || librarySelectedTags.length > 0 || librarySelectedCategoryId
                      ? "No items match your filters"
                      : "No media or custom meditations yet. Add some in Settings!"}
                  </div>
                ) : (
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleLibraryDragEnd}
                  >
                    <SortableContext
                      items={libraryItems.map(item => `${item.type}-${item.id}`)}
                      strategy={verticalListSortingStrategy}
                    >
                      <div className="space-y-2 max-h-96 overflow-y-auto">
                        {libraryItems.map((item) => (
                          <SortableLibraryItem
                            key={`${item.type}-${item.id}`}
                            item={item}
                            isMobile={isMobile}
                            categories={mnmCategories}
                            onCategoryChange={(categoryId) => updateMnmCategoryMutation.mutate({
                              id: item.id,
                              type: item.type,
                              mnmCategoryId: categoryId
                            })}
                            onToggleStar={() => toggleStarMutation.mutate({
                              id: item.id,
                              type: item.type,
                              isStarred: !item.isStarred
                            })}
                            onEdit={() => {
                              console.log("onEdit called for item:", item.id, item.title);
                              setEditingLibraryItem(item);
                            }}
                            onPlay={() => {
                              console.log("onPlay called for item:", item.id, item.title, "url:", item.url);
                              if (item.type === 'song' && item.url) {
                                if (isYouTubeUrl(item.url)) {
                                  console.log("Playing YouTube video:", item.url);
                                  const videoId = extractYouTubeVideoId(item.url);
                                  if (videoId) {
                                    handleStop();
                                    setYoutubeEmbedError(false);
                                    setPlayMode("youtube");
                                    setCurrentYouTubeVideoId(videoId);
                                    setCurrentYouTubeSong({ title: item.title, artist: item.artist || undefined });
                                    createSessionMutation.mutate({
                                      type: "youtube",
                                      sourceId: item.id,
                                      sourceName: item.title,
                                    });
                                  }
                                } else {
                                  handleStop();
                                  setPlayMode("single");
                                  setCurrentSingleSong({ title: item.title, artist: item.artist || undefined });
                                  playerControls.loadPlaylist([{
                                    id: item.id,
                                    title: item.title,
                                    artist: item.artist || undefined,
                                    url: item.url,
                                    duration: item.duration || undefined,
                                  }], 0);
                                  playerControls.play();
                                  createSessionMutation.mutate({
                                    type: "single",
                                    sourceId: item.id,
                                    sourceName: item.title,
                                  });
                                }
                              } else if (item.type === 'custom') {
                                const customMeditation = customMeditations.find(m => m.id === item.id);
                                if (customMeditation) {
                                  handleStop();
                                  setPlayMode("custom");
                                  setSelectedMeditation(customMeditation.id);
                                  createSessionMutation.mutate({
                                    type: "custom",
                                    sourceId: customMeditation.id,
                                    sourceName: customMeditation.name,
                                  });
                                }
                              }
                            }}
                          />
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Edit Library Item Dialog - Full Screen */}
      <Dialog open={!!editingLibraryItem} onOpenChange={(open) => !open && setEditingLibraryItem(null)}>
        <DialogContent className="w-screen h-screen max-w-none max-h-none m-0 rounded-none overflow-y-auto" data-testid="dialog-edit-library-item">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-6 h-6 text-primary" />
              Edit {editingLibraryItem?.type === 'song' ? 'Media Track' : 'Custom Meditation'}
            </DialogTitle>
            <DialogDescription>
              Update all details for this item
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 py-4 max-w-4xl">
            {/* Left Column - Basic Info */}
            <div className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Basic Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit-title">Title *</Label>
                    <Input
                      id="edit-title"
                      value={editFormData.title}
                      onChange={(e) => setEditFormData({ ...editFormData, title: e.target.value })}
                      placeholder="Enter title"
                      data-testid="input-edit-title"
                    />
                  </div>

                  {editingLibraryItem?.type === 'song' && (
                    <>
                      <div className="space-y-2">
                        <Label htmlFor="edit-artist">Artist</Label>
                        <Input
                          id="edit-artist"
                          value={editFormData.artist}
                          onChange={(e) => setEditFormData({ ...editFormData, artist: e.target.value })}
                          placeholder="Enter artist name"
                          data-testid="input-edit-artist"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="edit-url">URL</Label>
                        <Input
                          id="edit-url"
                          value={editFormData.url || ""}
                          onChange={(e) => setEditFormData({ ...editFormData, url: e.target.value })}
                          placeholder="YouTube URL or audio file URL"
                          data-testid="input-edit-url"
                        />
                        <p className="text-xs text-muted-foreground">
                          Supports YouTube links or direct audio file URLs
                        </p>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="edit-duration">Duration (seconds)</Label>
                        <NumberField
                          id="edit-duration"
                          min={0}
                          value={editFormData.duration}
                          onValueChange={(duration) => setEditFormData({ ...editFormData, duration })}
                          placeholder="Duration in seconds"
                          data-testid="input-edit-duration"
                        />
                      </div>
                    </>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="edit-subject">Subject</Label>
                    <Input
                      id="edit-subject"
                      value={editFormData.subject}
                      onChange={(e) => setEditFormData({ ...editFormData, subject: e.target.value })}
                      placeholder="Enter subject/topic"
                      data-testid="input-edit-subject"
                    />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Right Column - Organization */}
            <div className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Organization</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Content Type</Label>
                    <Select
                      value={editFormData.contentType}
                      onValueChange={(value) => setEditFormData({ ...editFormData, contentType: value })}
                    >
                      <SelectTrigger data-testid="select-edit-content-type">
                        <SelectValue placeholder="Select content type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="meditation">Meditation</SelectItem>
                        <SelectItem value="media">Media</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      Only meditation content counts toward meditation goals and time
                    </p>
                  </div>

                  {editingLibraryItem?.type === 'song' && (
                    <div className="space-y-2">
                      <Label>Playlist</Label>
                      <Select
                        value={editFormData.playlistId || "none"}
                        onValueChange={(value) => setEditFormData({ ...editFormData, playlistId: value === "none" ? undefined : value })}
                      >
                        <SelectTrigger data-testid="select-edit-playlist">
                          <SelectValue placeholder="Select playlist" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No Playlist</SelectItem>
                          {playlists.map((playlist) => (
                            <SelectItem key={playlist.id} value={playlist.id}>
                              {playlist.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label>Category</Label>
                    <Select
                      value={editFormData.mnmCategoryId || "none"}
                      onValueChange={(value) => setEditFormData({ ...editFormData, mnmCategoryId: value === "none" ? undefined : value })}
                    >
                      <SelectTrigger data-testid="select-edit-category">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No Category</SelectItem>
                        {mnmCategories.map((category) => (
                          <SelectItem key={category.id} value={category.id}>
                            <div className="flex items-center gap-2">
                              <div 
                                className="w-3 h-3 rounded-full" 
                                style={{ backgroundColor: category.color }}
                              />
                              {category.name}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Starred</Label>
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        variant={editFormData.isStarred ? "default" : "outline"}
                        size="sm"
                        onClick={() => setEditFormData({ ...editFormData, isStarred: !editFormData.isStarred })}
                        data-testid="button-toggle-starred"
                      >
                        <Star className={`w-4 h-4 mr-1 ${editFormData.isStarred ? 'fill-yellow-400' : ''}`} />
                        {editFormData.isStarred ? "Starred" : "Not Starred"}
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Tags</Label>
                    <div className="flex flex-wrap gap-1 mb-2 min-h-[32px]">
                      {editFormData.tags.map((tag) => (
                        <Badge key={tag} variant="secondary" className="flex items-center gap-1">
                          {tag}
                          <X
                            className="w-3 h-3 cursor-pointer"
                            onClick={() => setEditFormData({
                              ...editFormData,
                              tags: editFormData.tags.filter(t => t !== tag)
                            })}
                          />
                        </Badge>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <Input
                        placeholder="Add a tag"
                        value={editFormData.newTag}
                        onChange={(e) => setEditFormData({ ...editFormData, newTag: e.target.value })}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && editFormData.newTag.trim()) {
                            e.preventDefault();
                            if (!editFormData.tags.includes(editFormData.newTag.trim())) {
                              setEditFormData({
                                ...editFormData,
                                tags: [...editFormData.tags, editFormData.newTag.trim()],
                                newTag: ""
                              });
                            }
                          }
                        }}
                        data-testid="input-edit-new-tag"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          if (editFormData.newTag.trim() && !editFormData.tags.includes(editFormData.newTag.trim())) {
                            setEditFormData({
                              ...editFormData,
                              tags: [...editFormData.tags, editFormData.newTag.trim()],
                              newTag: ""
                            });
                          }
                        }}
                        data-testid="button-add-tag"
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          <DialogFooter className="flex gap-2 pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => setEditingLibraryItem(null)}
              data-testid="button-cancel-edit"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveLibraryItem}
              disabled={updateLibraryItemMutation.isPending || !editFormData.title.trim()}
              data-testid="button-save-edit"
            >
              {updateLibraryItemMutation.isPending ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Playlist Customization Dialog */}
      <Dialog open={showPlaylistDialog} onOpenChange={setShowPlaylistDialog}>
        <DialogContent className="w-screen h-screen max-w-none max-h-none m-0 rounded-none overflow-y-auto bg-background" data-testid="dialog-customize-playlists">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ListMusic className="w-6 h-6 text-primary" />
              Customize Playlists
            </DialogTitle>
            <DialogDescription>
              Create and manage your media playlists
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Create New Playlist */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Create New Playlist</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex gap-2">
                  <Input
                    placeholder="Enter playlist name..."
                    value={newPlaylistName}
                    onChange={(e) => setNewPlaylistName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && newPlaylistName.trim()) {
                        createPlaylistMutation.mutate({ name: newPlaylistName.trim() });
                      }
                    }}
                    data-testid="input-new-playlist-name"
                  />
                  <Button
                    onClick={() => {
                      if (newPlaylistName.trim()) {
                        createPlaylistMutation.mutate({ name: newPlaylistName.trim() });
                      }
                    }}
                    disabled={!newPlaylistName.trim() || createPlaylistMutation.isPending}
                    data-testid="button-create-playlist"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    {createPlaylistMutation.isPending ? "Creating..." : "Create"}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Existing Playlists */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">My Playlists</CardTitle>
                <CardDescription>
                  Click on a playlist to manage its media in the settings page
                </CardDescription>
              </CardHeader>
              <CardContent>
                {playlists.length === 0 ? (
                  <div className="text-center py-8">
                    <ListMusic className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                    <p className="text-muted-foreground">No playlists yet. Create your first one above!</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {playlists.map((playlist) => (
                      <PlaylistListItem 
                        key={playlist.id}
                        playlist={playlist}
                        allSongs={allSongsFromAllPlaylists}
                        onAddExistingSong={(songId, playlistId) => {
                          moveSongToPlaylistMutation.mutate({ songId, newPlaylistId: playlistId });
                        }}
                        onRemoveSong={(songId) => {
                          deleteSongMutation.mutate(songId);
                        }}
                        onManage={() => {
                          setShowPlaylistDialog(false);
                          setLocation("/meditation/settings");
                        }}
                        onDelete={() => {
                          if (confirm(`Are you sure you want to delete "${playlist.name}"? This will also delete all media in this playlist.`)) {
                            deletePlaylistMutation.mutate(playlist.id);
                          }
                        }}
                      />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPlaylistDialog(false)} data-testid="button-close-playlist-dialog">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Playlist List Item Component for the dialog
function PlaylistListItem({ 
  playlist, 
  onManage, 
  onDelete,
  allSongs,
  onAddExistingSong,
  onRemoveSong,
}: { 
  playlist: MeditationPlaylist; 
  onManage: () => void;
  onDelete: () => void;
  allSongs: MeditationSong[];
  onAddExistingSong: (songId: string, playlistId: string) => void;
  onRemoveSong: (songId: string) => void;
}) {
  const { data: playlistSongs = [] } = useQuery<MeditationSong[]>({
    queryKey: ["/api/meditation/playlists", playlist.id, "songs"],
  });
  const [expanded, setExpanded] = useState(false);
  const [showAddMediaDialog, setShowAddMediaDialog] = useState(false);
  
  const availableSongs = allSongs.filter(
    song => song.playlistId !== playlist.id
  );

  return (
    <div 
      className="border rounded-lg overflow-hidden"
      data-testid={`playlist-item-${playlist.id}`}
    >
      {/* Playlist Header */}
      <div 
        className="flex items-center justify-between p-4 hover:bg-accent/50 transition-colors cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <ListMusic className="w-5 h-5 text-primary" />
          <div>
            <h4 className="font-medium">{playlist.name}</h4>
            <p className="text-sm text-muted-foreground">
              {playlistSongs.length} {playlistSongs.length === 1 ? 'track' : 'tracks'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={(e) => { e.stopPropagation(); setShowAddMediaDialog(true); }}
            data-testid={`button-add-media-${playlist.id}`}
          >
            <Plus className="w-4 h-4 mr-1" />
            Add Media
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={(e) => { e.stopPropagation(); onManage(); }}
            data-testid={`button-manage-${playlist.id}`}
          >
            <Settings className="w-4 h-4 mr-1" />
            Settings
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="text-destructive hover:text-destructive"
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            data-testid={`button-delete-${playlist.id}`}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
          <ChevronDown className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </div>
      </div>
      
      {/* Expanded Track List */}
      {expanded && playlistSongs.length > 0 && (
        <div className="border-t bg-muted/30 p-3 space-y-2">
          {playlistSongs.map((song) => {
            const isYouTube = song.url?.includes('youtube.com') || song.url?.includes('youtu.be');
            return (
              <div 
                key={song.id}
                className="flex items-center gap-3 p-2 rounded-md bg-background hover:bg-accent/50 transition-colors"
              >
                {isYouTube ? (
                  <Youtube className="w-4 h-4 text-red-500 shrink-0" />
                ) : (
                  <Music className="w-4 h-4 text-primary shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{song.title}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {song.artist || "Unknown Artist"}
                    {song.duration && ` • ${Math.floor(song.duration / 60)}:${String(song.duration % 60).padStart(2, '0')}`}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-destructive hover:text-destructive shrink-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (confirm(`Remove "${song.title}" from this playlist?`)) {
                      onRemoveSong(song.id);
                    }
                  }}
                >
                  <X className="w-3 h-3" />
                </Button>
              </div>
            );
          })}
        </div>
      )}
      
      {/* Empty state when expanded but no tracks */}
      {expanded && playlistSongs.length === 0 && (
        <div className="border-t bg-muted/30 p-4 text-center text-sm text-muted-foreground">
          <p>No tracks in this playlist yet</p>
          <Button
            variant="link"
            size="sm"
            className="mt-2"
            onClick={() => setShowAddMediaDialog(true)}
          >
            <Plus className="w-4 h-4 mr-1" />
            Add Media
          </Button>
        </div>
      )}
      
      {/* Add Media Dialog */}
      <Dialog open={showAddMediaDialog} onOpenChange={setShowAddMediaDialog}>
        <DialogContent className="w-screen h-screen max-w-none max-h-none m-0 rounded-none overflow-y-auto" onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle>Add Media to "{playlist.name}"</DialogTitle>
            <DialogDescription>
              Create new media or choose from your existing library
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 max-h-[60vh] overflow-y-auto space-y-4">
            {/* Create New Media Section */}
            <div className="border rounded-lg p-4 bg-primary/5">
              <h4 className="font-medium mb-2 flex items-center gap-2">
                <Plus className="w-4 h-4" />
                Create New Media
              </h4>
              <p className="text-sm text-muted-foreground mb-3">
                Add a new audio file or YouTube video to this playlist
              </p>
              <Button
                onClick={() => {
                  setShowAddMediaDialog(false);
                  onManage();
                }}
                className="w-full"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create New Media Track
              </Button>
            </div>
            
            {/* Existing Media Section */}
            {availableSongs.length > 0 && (
              <div>
                <h4 className="font-medium mb-2 flex items-center gap-2">
                  <Music className="w-4 h-4" />
                  Add Existing Media
                </h4>
                <p className="text-sm text-muted-foreground mb-3">
                  Move media from other playlists to this one:
                </p>
                <div className="space-y-2">
                  {availableSongs.map((song) => {
                    const isYouTube = song.url?.includes('youtube.com') || song.url?.includes('youtu.be');
                    return (
                      <div 
                        key={song.id}
                        className="flex items-center gap-3 p-3 rounded-md border hover:bg-accent/50 cursor-pointer transition-colors"
                        onClick={() => {
                          onAddExistingSong(song.id, playlist.id);
                          setShowAddMediaDialog(false);
                        }}
                      >
                        {isYouTube ? (
                          <Youtube className="w-4 h-4 text-red-500 shrink-0" />
                        ) : (
                          <Music className="w-4 h-4 text-primary shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{song.title}</p>
                          <p className="text-xs text-muted-foreground truncate">
                            {song.artist || "Unknown Artist"}
                          </p>
                        </div>
                        <Plus className="w-4 h-4 text-primary shrink-0" />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            
            {/* Empty state for existing media */}
            {availableSongs.length === 0 && (
              <div className="text-center py-4 text-muted-foreground border rounded-lg">
                <p className="text-sm">No other media available to move here</p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowAddMediaDialog(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
